import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Edit2, Save, X, ChevronDown, ChevronUp, AlertCircle, CheckCircle, Loader, Upload, Wand2, Plus, Minus, Trash2, History, Sparkles } from 'lucide-react';
import { API_ENDPOINTS } from '../../constants/api';
import { apiFetch } from '../../utils/apiClient';
import { announceJob, waitForJobResult } from '../../services/jobs';

const VERSIONING_ENABLED = true;
const MAX_MANUAL_NORMALIZE_SELECTION = 10;
const TEST_CASE_DRAFT_AUTOSAVE_DELAY_MS = 900;

const cleanExcelCaseTitle = (value) => {
  const normalized = String(value || '')
    .replace(/\s+/g, ' ')
    .replace(/^\s*(test\s+condition|scenario|objective)\s*[:-]\s*/i, '')
    .replace(/^\s*(verify|validate|ensure|check|confirm)\s+(that\s+)?(the\s+)?/i, '')
    .trim();
  if (!normalized || /^\d+$/.test(normalized)) return '';
  const [candidate] = normalized.split(/\s+\b(is|are|can|should|shall|must|with|for|successfully|visible|displayed)\b\s+/i);
  const title = String(candidate || normalized).trim();
  return title.length > 80 ? `${title.slice(0, 77).trim()}...` : title;
};

const resolveCaseHeaderTitle = (testCase, index, isExcelCase) => {
  const caseNo = String(testCase?.test_case_no || testCase?.testcase_no || '').trim();
  const isGeneratedNumericExcelId = isExcelCase && /^\d+$/.test(caseNo);
  if (caseNo && !isGeneratedNumericExcelId) return caseNo;

  return (
    cleanExcelCaseTitle(testCase?.Module || testCase?.module)
    || cleanExcelCaseTitle(testCase?.test_case_title || testCase?.title)
    || cleanExcelCaseTitle(testCase?.test_description)
    || `Test Case ${index + 1}`
  );
};

export const EditTestCasesStep = ({
  processingResult,
  pdfId,
  selectedSection,
  currentUser,
  requestedArtifactVersion,
  initialEditedTestCases,
  initialBaselineTestCases,
  status,
  onEditComplete,
  onSaveTestCases,
  onGenerateMore,
  error,
  sourceLabel = 'BRD',
  enableVersioning = true,
  enableManualImport = true,
  allowEmptyManualImport = false,
  showSourceBreakdown = true,
  showVersionPanel = true,
  normalizationContext = null,
  continueTargetLabel = 'save/download step',
}) => {
  const versioningEnabled = VERSIONING_ENABLED && enableVersioning && showVersionPanel;
  const sourceLabelText = sourceLabel || 'BRD';
  const manualImportScopeLabel = sourceLabelText.toLowerCase() === 'brd'
    ? 'BRD section'
    : sourceLabelText.toLowerCase();
  const hasAdvancedActions = Boolean(onGenerateMore || enableManualImport);
  const [categorizedTestCases, setCategorizedTestCases] = useState({
    positive: [],
    negative: [],
    all: [],
  });
  const [baselineTestCases, setBaselineTestCases] = useState({
    positive: [],
    negative: [],
    all: [],
  });
  const [testCaseStructure, setTestCaseStructure] = useState('categorized');
  const [expandedCase, setExpandedCase] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [importError, setImportError] = useState(null);
  const [importStatus, setImportStatus] = useState(null);
  const [importTargetCategory, setImportTargetCategory] = useState('positive');
  const [isImporting, setIsImporting] = useState(false);
  const [isNormalizingManualCases, setIsNormalizingManualCases] = useState(false);
  const [normalizingManualAction, setNormalizingManualAction] = useState(null);
  const [normalizingManualCaseKeys, setNormalizingManualCaseKeys] = useState([]);
  const [normalizeManualError, setNormalizeManualError] = useState(null);
  const [normalizeManualStatus, setNormalizeManualStatus] = useState(null);
  const MAX_ADDITIONAL_PER_TYPE = 5;
  const initialAdditionalPositive = Math.max(
    0,
    Math.min(
      MAX_ADDITIONAL_PER_TYPE,
      Number(processingResult?.suggested_additional?.positive_count || 1)
    )
  );
  const initialAdditionalNegative = Math.max(
    0,
    Math.min(
      MAX_ADDITIONAL_PER_TYPE,
      Number(processingResult?.suggested_additional?.negative_count || 1)
    )
  );
  const [morePositive, setMorePositive] = useState(
    initialAdditionalPositive
  );
  const [moreNegative, setMoreNegative] = useState(
    initialAdditionalNegative
  );
  const [coveragePrompt, setCoveragePrompt] = useState('');
  const [generateMoreError, setGenerateMoreError] = useState(null);
  const [generateMoreStatus, setGenerateMoreStatus] = useState(null);
  const [isGenerateMoreModalOpen, setIsGenerateMoreModalOpen] = useState(false);
  const [pendingRemoveCase, setPendingRemoveCase] = useState(null);
  const [selectedCaseKeys, setSelectedCaseKeys] = useState([]);
  const [manualImportMeta, setManualImportMeta] = useState(null);
  const [versionHistory, setVersionHistory] = useState([]);
  const requestedArtifactVersionRef = useRef(null);
  const [activeVersion, setActiveVersion] = useState(null);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [versionError, setVersionError] = useState(null);
  const [isVersionPanelOpen, setIsVersionPanelOpen] = useState(false);
  const [lastSavedVersion, setLastSavedVersion] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [loadedVersion, setLoadedVersion] = useState(null);
  const [isSaveOptionsOpen, setIsSaveOptionsOpen] = useState(false);
  const [expandedInputSetsByCase, setExpandedInputSetsByCase] = useState({});
  const [activeCaseTab, setActiveCaseTab] = useState('ai-positive');
  const [isAdvancedActionsOpen, setIsAdvancedActionsOpen] = useState(false);
  const importInputRef = useRef(null);
  const hasChangesRef = useRef(false);
  const appliedEnrichmentRef = useRef('');
  const appliedStreamSignatureRef = useRef('');
  const autoMissingCoverageSignatureRef = useRef('');
  const lastDraftAutosaveSignatureRef = useRef('');
  const draftAutosaveInFlightSignatureRef = useRef('');
  const flushDraftSnapshotRef = useRef(() => {});

  useEffect(() => {
    hasChangesRef.current = hasChanges;
  }, [hasChanges]);

  const formatTimestamp = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString();
  };
  const normalizeStringList = (value) => (
    Array.isArray(value)
      ? value.map((item) => String(item || '').trim()).filter(Boolean)
      : []
  );
  const normalizeCoverageScore = (...values) => {
    for (const value of values) {
      if (value === null || value === undefined || String(value).trim() === '') continue;
      const numericValue = Number(value);
      if (Number.isFinite(numericValue)) {
        return Math.max(0, Math.min(100, Math.round(numericValue)));
      }
    }
    return null;
  };
  const normalizeMissingAreas = (value) => (
    Array.isArray(value)
      ? value
        .map((item) => {
          if (typeof item === 'string') {
            const description = item.trim();
            return description ? { type: 'gap', description, suggested_count: 1 } : null;
          }
          if (!item || typeof item !== 'object') return null;
          const description = String(item.description || item.gap || item.summary || '').trim();
          if (!description) return null;
          const suggestedCount = Math.max(0, Number(item.suggested_count || item.suggestedCount || 0));
          return {
            type: String(item.type || item.category || 'gap').trim() || 'gap',
            description,
            suggested_count: Number.isFinite(suggestedCount) ? Math.floor(suggestedCount) : 0,
          };
        })
        .filter(Boolean)
      : []
  );
  const normalizeEnrichmentStatus = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (['pending', 'queued', 'running', 'completed', 'failed'].includes(normalized)) {
      return normalized;
    }
    return '';
  };
  const extractEnrichmentMeta = (source = {}) => {
    const payload = source?.payload && typeof source.payload === 'object'
      ? source.payload
      : (source?.test_cases && typeof source.test_cases === 'object' ? source.test_cases : source);
    const metrics = source?.metrics?.enrichment && typeof source.metrics.enrichment === 'object'
      ? source.metrics.enrichment
      : {};
    const status = normalizeEnrichmentStatus(
      payload?.enrichment_status
      || source?.enrichment_status
      || metrics?.status
    );
    const inputSetsStatus = normalizeEnrichmentStatus(
      payload?.input_sets_status
      || source?.input_sets_status
      || metrics?.input_sets_status
    );
    const gapStatus = normalizeEnrichmentStatus(
      payload?.gap_analysis_status
      || source?.gap_analysis_status
      || metrics?.gap_analysis_status
    );
    const resolvedCoverageScore = normalizeCoverageScore(
      payload?.coverage_score,
      source?.coverage_score,
      metrics?.coverage_score
    );
    return {
      status,
      inputSetsStatus,
      gapStatus,
      enrichedAt: payload?.enriched_at || source?.enriched_at || metrics?.enriched_at || null,
      coverageScore: gapStatus === 'completed' ? resolvedCoverageScore : null,
      coverageGaps: normalizeStringList(payload?.coverage_gaps).length > 0
        ? normalizeStringList(payload.coverage_gaps)
        : (normalizeStringList(source?.coverage_gaps).length > 0
          ? normalizeStringList(source.coverage_gaps)
          : normalizeStringList(metrics?.coverage_gaps)),
      missingAreas: normalizeMissingAreas(payload?.missing_areas).length > 0
        ? normalizeMissingAreas(payload.missing_areas)
        : (normalizeMissingAreas(source?.missing_areas).length > 0
          ? normalizeMissingAreas(source.missing_areas)
          : normalizeMissingAreas(metrics?.missing_areas)),
      duplicates: normalizeStringList(payload?.duplicates).length > 0
        ? normalizeStringList(payload.duplicates)
        : (normalizeStringList(source?.duplicates).length > 0
          ? normalizeStringList(source.duplicates)
          : normalizeStringList(metrics?.duplicates)),
      weakCases: normalizeStringList(payload?.weak_cases).length > 0
        ? normalizeStringList(payload.weak_cases)
        : (normalizeStringList(source?.weak_cases).length > 0
          ? normalizeStringList(source.weak_cases)
          : normalizeStringList(metrics?.weak_cases)),
      suggestedAdditional:
        payload?.suggested_additional
        || source?.suggested_additional
        || metrics?.suggested_additional
        || null,
      error: metrics?.error || source?.enrichment_error || '',
    };
  };
  const [enrichmentMeta, setEnrichmentMeta] = useState(() => extractEnrichmentMeta(processingResult));
  const enrichmentStatus = enrichmentMeta.status;
  const isEnrichmentPending = ['pending', 'queued', 'running'].includes(enrichmentStatus);
  const isEnrichmentComplete = enrichmentStatus === 'completed';
  const isEnrichmentFailed = enrichmentStatus === 'failed';
  const enrichmentCompletedAt = formatTimestamp(enrichmentMeta.enrichedAt);
  const [enrichmentNotice, setEnrichmentNotice] = useState('');
  const normalizeManualImportMeta = (value) => {
    if (!value || typeof value !== 'object') return null;
    const fileName = String(value.file_name || value.filename || '').trim();
    if (!fileName) return null;
    const importedAt = String(value.imported_at || value.created_at || '').trim();
    const importedByName = String(
      value.imported_by_name || value.importer_name || value.uploaded_by_name || ''
    ).trim();
    const importedByEmail = String(
      value.imported_by_email || value.importer_email || value.uploaded_by_email || ''
    ).trim();
    const importedBy = String(
      value.imported_by || importedByName || importedByEmail || value.imported_by_id || ''
    ).trim();
    const importedCountRaw = Number(value.imported_count ?? value.count ?? 0);
    const importedCount = Number.isFinite(importedCountRaw) && importedCountRaw > 0
      ? Math.floor(importedCountRaw)
      : 0;
    return {
      file_name: fileName,
      imported_at: importedAt || new Date().toISOString(),
      imported_count: importedCount,
      imported_by: importedBy || null,
      imported_by_name: importedByName || null,
      imported_by_email: importedByEmail || null,
    };
  };
  const isInternalActorId = (value) => {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) return false;
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(normalizedValue)) {
      return true;
    }
    if (/^[0-9a-f]{24}$/i.test(normalizedValue)) {
      return true;
    }
    return false;
  };
  const normalizeActorCandidate = (candidate) => {
    if (candidate === null || candidate === undefined) return '';

    if (
      typeof candidate === 'string'
      || typeof candidate === 'number'
      || typeof candidate === 'boolean'
    ) {
      return String(candidate).trim();
    }

    if (Array.isArray(candidate)) {
      for (const item of candidate) {
        const resolved = normalizeActorCandidate(item);
        if (resolved) return resolved;
      }
      return '';
    }

    if (typeof candidate === 'object') {
      const preferredKeys = [
        'name',
        'display_name',
        'full_name',
        'username',
        'user_name',
        'email',
        'label',
        'value',
        'id',
        'user_id',
      ];
      for (const key of preferredKeys) {
        const resolved = normalizeActorCandidate(candidate?.[key]);
        if (resolved) return resolved;
      }
    }

    return '';
  };
  const resolveActorLabel = (...values) => {
    for (const candidate of values) {
      const normalizedValue = normalizeActorCandidate(candidate);
      if (!normalizedValue) continue;
      if (isInternalActorId(normalizedValue)) continue;
      return normalizedValue;
    }
    return null;
  };

  const resolvedGeneratedAt =
    processingResult?.generated_at ||
    processingResult?.created_at ||
    processingResult?.updated_at ||
    null;
  const resolvedEditedAt =
    processingResult?.edited_at ||
    null;
  const currentActorLabel = resolveActorLabel(
    currentUser?.name,
    currentUser?.email,
    currentUser?.username,
    currentUser?.user_name,
    currentUser?.display_name
  );
  const resolvedGeneratedBy = resolveActorLabel(
    processingResult?.generated_by_name,
    processingResult?.generated_by_email,
    processingResult?.generated_by,
    processingResult?.request_payload?.generated_by_name,
    processingResult?.request_payload?.generated_by_email,
    processingResult?.request_payload?.generated_by,
    processingResult?.payload?.generated_by_name,
    processingResult?.payload?.generated_by_email,
    processingResult?.payload?.generated_by,
    currentActorLabel
  );
  const resolvedEditedBy = resolveActorLabel(
    processingResult?.edited_by_name,
    processingResult?.edited_by_email,
    processingResult?.edited_by,
    processingResult?.request_payload?.edited_by_name,
    processingResult?.request_payload?.edited_by_email,
    processingResult?.request_payload?.edited_by,
    processingResult?.payload?.edited_by_name,
    processingResult?.payload?.edited_by_email,
    processingResult?.payload?.edited_by
  );

  const normalizeCaseForComparison = (testCase) => {
    if (!testCase || typeof testCase !== 'object') return testCase;
    const {
      _source,
      _streaming,
      _streaming_input_sets,
      source_type,
      _renamed_from,
      _category,
      _case_key,
      ...rest
    } = testCase;
    return rest;
  };

  const stripInternalCaseFields = (testCase = {}) => {
    if (!testCase || typeof testCase !== 'object') return testCase;
    return Object.fromEntries(
      Object.entries(testCase).filter(([key]) => !key.startsWith('_'))
    );
  };

  const sanitizeCasesForPersistence = (casesPayload) => {
    if (Array.isArray(casesPayload)) {
      return casesPayload.map(stripInternalCaseFields);
    }
    if (!casesPayload || typeof casesPayload !== 'object') {
      return casesPayload;
    }

    const sanitized = {};
    if ('positive' in casesPayload) {
      sanitized.positive = Array.isArray(casesPayload.positive)
        ? casesPayload.positive.map(stripInternalCaseFields)
        : [];
    }
    if ('negative' in casesPayload) {
      sanitized.negative = Array.isArray(casesPayload.negative)
        ? casesPayload.negative.map(stripInternalCaseFields)
        : [];
    }
    if ('all' in casesPayload) {
      sanitized.all = Array.isArray(casesPayload.all)
        ? casesPayload.all.map(stripInternalCaseFields)
        : [];
    }
    return sanitized;
  };

  const flattenBaselineCases = useCallback(() => {
    if (testCaseStructure === 'categorized') {
      return [
        ...(baselineTestCases.positive || []),
        ...(baselineTestCases.negative || []),
      ];
    }
    return baselineTestCases.all || [];
  }, [baselineTestCases, testCaseStructure]);

  const getBaselineCaseFor = useCallback((testCase) => {
    const baseId = testCase?.test_case_no || testCase?._renamed_from;
    if (!baseId) return null;
    const pool = flattenBaselineCases();
    return (
      pool.find((candidate) =>
        candidate?.test_case_no === baseId || candidate?._renamed_from === baseId
      ) || null
    );
  }, [flattenBaselineCases]);

  const isEditedCase = useCallback((testCase) => {
    if (!testCase) return false;
    const baseline = getBaselineCaseFor(testCase);
    if (!baseline) return false;
    const currentNorm = normalizeCaseForComparison(testCase);
    const baselineNorm = normalizeCaseForComparison(baseline);
    return JSON.stringify(currentNorm) !== JSON.stringify(baselineNorm);
  }, [getBaselineCaseFor]);

  const hasCategorizedTestCases = (testCases) =>
    testCases &&
    typeof testCases === 'object' &&
    !Array.isArray(testCases) &&
    ('positive' in testCases || 'negative' in testCases);

  const MAX_INPUT_SETS_PER_CASE = 3;
  const cloneCases = (value) => JSON.parse(JSON.stringify(value));
  const normalizeUserInputEntry = (item = {}) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      return { field: '', value: '' };
    }

    return {
      ...item,
      field: String(item.field || item.name || item.label || '').trim(),
      value: item.value === null || item.value === undefined ? '' : String(item.value),
    };
  };
  const normalizeUserInputList = (items = []) => (Array.isArray(items) ? items : [])
    .filter((item) => item && typeof item === 'object' && !Array.isArray(item))
    .map((item) => normalizeUserInputEntry(item));
  const buildEmptyInputTemplateFromSets = (sets = [], preferredIndex = 0) => {
    const normalizedSets = (Array.isArray(sets) ? sets : []).map((setItem) =>
      normalizeUserInputList(setItem?.user_inputs || setItem?.inputs || setItem?.values || [])
    );
    const preferredRows = normalizedSets[preferredIndex] || [];
    const templateRows = preferredRows.length > 0
      ? preferredRows
      : (normalizedSets.find((rows) => rows.length > 0) || []);

    return templateRows.map((row) => ({
      field: String(row?.field || row?.name || row?.label || '').trim(),
      value: '',
    }));
  };
  const clampInputSetIndex = (value, maxLength) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return 0;
    const index = Math.max(0, Math.floor(numeric));
    return Math.min(index, Math.max(0, maxLength - 1));
  };
  const normalizeUserInputSetsForCase = (testCase = {}) => {
    const rawSets = Array.isArray(testCase?.user_input_sets) ? testCase.user_input_sets : [];
    const normalizedSets = rawSets
      .map((set, index) => {
        if (!set || typeof set !== 'object' || Array.isArray(set)) return null;
        const setInputs = normalizeUserInputList(
          set.user_inputs || set.inputs || set.values || []
        );
        return {
          set_name: String(set.set_name || set.name || `Set ${index + 1}`).trim() || `Set ${index + 1}`,
          set_type: String(set.set_type || set.scenario_type || '').trim(),
          user_inputs: setInputs,
        };
      })
      .filter(Boolean)
      .slice(0, MAX_INPUT_SETS_PER_CASE);

    const fallbackInputs = normalizeUserInputList(testCase?.user_inputs || []);
    const resolvedSets = normalizedSets.length > 0
      ? normalizedSets
      : (fallbackInputs.length > 0
        ? [
          {
            set_name: 'Set 1',
            set_type: 'primary',
            user_inputs: fallbackInputs,
          },
        ]
        : []);

    const activeIndex = clampInputSetIndex(
      testCase?.active_input_set_index ?? 0,
      resolvedSets.length || 1
    );
    const activeInputs = resolvedSets[activeIndex]?.user_inputs || fallbackInputs || [];

    return {
      ...testCase,
      user_input_sets: resolvedSets,
      active_input_set_index: activeIndex,
      user_inputs: activeInputs,
    };
  };
  const normalizeSourceType = (value) => String(value || '').trim().toLowerCase();
  const excelImportedSourceTypes = new Set([
    'testcase_excel',
    'test_case_excel',
    'excel_test_cases',
  ]);
  const isExcelImportedSourceType = (value) => (
    excelImportedSourceTypes.has(normalizeSourceType(value))
  );
  const isUserImportedSourceType = (value) => (
    normalizeSourceType(value) === 'manual_upload'
    || isExcelImportedSourceType(value)
  );
  const resolveCaseSourceType = (testCase = {}, sourceType = '') => normalizeSourceType(
    sourceType
    || testCase?.source_type
    || testCase?.source?.source_type
    || testCase?.source?.type
    || processingResult?.source_type
    || processingResult?.item_type
  );
  const sourceKindForCase = (testCase = {}, sourceType = testCase?.source_type) => {
    const resolvedSourceType = resolveCaseSourceType(testCase, sourceType);
    if (isExcelImportedSourceType(resolvedSourceType) || testCase?._source === 'excel') return 'excel';
    if (resolvedSourceType === 'manual_upload' || testCase?._source === 'manual') {
      return 'manual';
    }
    return 'ai';
  };
  const backfillExcelModule = (testCase, sourceKind) => {
    if (sourceKind !== 'excel') return testCase;
    if (String(testCase?.Module || testCase?.module || '').trim()) return testCase;
    const module = (
      cleanExcelCaseTitle(testCase?.test_case_title || testCase?.title)
      || cleanExcelCaseTitle(testCase?.test_description)
    );
    if (!module) return testCase;
    const source = testCase?.source && typeof testCase.source === 'object'
      ? {
        ...testCase.source,
        module_inferred: testCase.source.module_inferred ?? true,
        module_inference_source: testCase.source.module_inference_source || 'editor-fallback',
      }
      : { module_inferred: true, module_inference_source: 'editor-fallback' };
    return {
      ...testCase,
      Module: module,
      module,
      source,
    };
  };
  const tagAiCases = (cases) => (cases || []).map((testCase) => {
    const sourceType = resolveCaseSourceType(testCase) || 'ai_generated';
    const normalizedCase = normalizeUserInputSetsForCase(testCase);
    const sourceKind = sourceKindForCase(testCase, sourceType);
    return backfillExcelModule({
      ...testCase,
      ...normalizedCase,
      source_type: sourceType,
      _source: sourceKind,
    }, sourceKind);
  });
  const hasCategoryMarker = (caseId, marker) => (
    new RegExp(`(^|[-_])${marker}([_-]|$)`, 'i').test(String(caseId || '').trim())
  );
  const categoryMarkerForPrefix = (prefix) => {
    const normalizedPrefix = String(prefix || '').toUpperCase();
    if (normalizedPrefix.includes('POS')) return 'POS';
    if (normalizedPrefix.includes('NEG')) return 'NEG';
    return null;
  };
  const resolveCaseIdForPrefix = (incomingId, prefix, index) => {
    const normalizedIncomingId = String(incomingId || '').trim();
    const marker = categoryMarkerForPrefix(prefix);

    if (normalizedIncomingId && (!marker || hasCategoryMarker(normalizedIncomingId, marker))) {
      return normalizedIncomingId;
    }

    return `${prefix}-${index + 1}`;
  };
  const normalizeCategorizedCaseIds = (casesBundle) => {
    const usedIds = new Set();

    const normalizeCategoryCases = (cases, marker) => {
      let sequence = 1;

      return (cases || []).map((testCase) => {
        const sourceType = (
          resolveCaseSourceType(testCase)
          || (testCase?._source === 'manual' ? 'manual_upload' : 'ai_generated')
        );
        const isExcelImport = isExcelImportedSourceType(sourceType);
        const categoryPrefix = normalizeSourceType(sourceType) === 'manual_upload'
          ? `MANUAL-${marker}`
          : isExcelImport
            ? `EXCEL-${marker}`
          : `AC-TC-${marker}`;

        const existingId = String(testCase?.test_case_no || '').trim();
        let candidate = (isExcelImport && existingId) || hasCategoryMarker(existingId, marker)
          ? existingId
          : `${categoryPrefix}-${sequence}`;

        while (usedIds.has(candidate)) {
          sequence += 1;
          candidate = isExcelImport && existingId
            ? `${existingId}-${sequence}`
            : `${categoryPrefix}-${sequence}`;
        }
        usedIds.add(candidate);

        sequence += 1;
        const normalizedCase = normalizeUserInputSetsForCase(testCase);
        const sourceKind = sourceKindForCase(testCase, sourceType);
        return backfillExcelModule({
          ...testCase,
          ...normalizedCase,
          source_type: sourceType,
          _source: sourceKind,
          test_case_no: candidate,
        }, sourceKind);
      });
    };

    return {
      ...casesBundle,
      positive: normalizeCategoryCases(casesBundle?.positive, 'POS'),
      negative: normalizeCategoryCases(casesBundle?.negative, 'NEG'),
      all: tagAiCases(casesBundle?.all || []),
    };
  };

  const normalizeArtifactPayloadToCasesBundle = (payload) => {
    if (Array.isArray(payload)) {
      return {
        structure: 'flat',
        cases: {
          positive: [],
          negative: [],
          all: tagAiCases(payload),
        },
      };
    }

    if (payload && typeof payload === 'object' && payload.test_cases) {
      return normalizeArtifactPayloadToCasesBundle(payload.test_cases);
    }

    if (payload && typeof payload === 'object' && payload.test_case && typeof payload.test_case === 'object') {
      const category = String(payload.category || payload.prompt_type || payload.test_case?.type || '').trim().toLowerCase();
      return {
        structure: 'categorized',
        cases: normalizeCategorizedCaseIds({
          positive: category === 'negative' ? [] : tagAiCases([payload.test_case]),
          negative: category === 'negative' ? tagAiCases([payload.test_case]) : [],
          all: [],
        }),
      };
    }

    if (payload && typeof payload === 'object' && ('positive' in payload || 'negative' in payload)) {
      return {
        structure: 'categorized',
        cases: normalizeCategorizedCaseIds({
          positive: tagAiCases(Array.isArray(payload.positive) ? payload.positive : Object.values(payload.positive || {})),
          negative: tagAiCases(Array.isArray(payload.negative) ? payload.negative : Object.values(payload.negative || {})),
          all: [],
        }),
      };
    }

    if (payload && typeof payload === 'object' && Array.isArray(payload.all)) {
      return {
        structure: 'flat',
        cases: {
          positive: [],
          negative: [],
          all: tagAiCases(payload.all),
        },
      };
    }

    return {
      structure: 'categorized',
      cases: { positive: [], negative: [], all: [] },
    };
  };

  const initialCasesBundleRef = useRef(null);
  if (!initialCasesBundleRef.current) {
    const hasAnyCases = (cases) => {
      if (!cases) return false;
      if (Array.isArray(cases)) return cases.length > 0;
      if (hasCategorizedTestCases(cases)) {
        return (cases.positive?.length || 0) + (cases.negative?.length || 0) > 0;
      }
      return true;
    };

    const sourceCases = hasAnyCases(initialEditedTestCases)
      ? initialEditedTestCases
      : processingResult?.test_cases;

    if (!sourceCases) {
      initialCasesBundleRef.current = {
        structure: 'categorized',
        cases: { positive: [], negative: [], all: [] },
      };
    } else if (Array.isArray(sourceCases)) {
      initialCasesBundleRef.current = {
        structure: 'flat',
        cases: {
          positive: [],
          negative: [],
          all: tagAiCases(sourceCases),
        },
      };
    } else if (typeof sourceCases === 'object' && ('positive' in sourceCases || 'negative' in sourceCases)) {
      const { positive = [], negative = [] } = sourceCases;
      const categorizedInitialCases = normalizeCategorizedCaseIds({
        positive: tagAiCases(Array.isArray(positive) ? positive : Object.values(positive)),
        negative: tagAiCases(Array.isArray(negative) ? negative : Object.values(negative)),
        all: [],
      });
      initialCasesBundleRef.current = {
        structure: 'categorized',
        cases: categorizedInitialCases,
      };
    } else {
      initialCasesBundleRef.current = {
        structure: 'flat',
        cases: {
          positive: [],
          negative: [],
          all: tagAiCases([sourceCases]),
        },
      };
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const initialCasesBundle = initialCasesBundleRef.current;
    const baselineSource = initialBaselineTestCases && (
      Array.isArray(initialBaselineTestCases)
      || hasCategorizedTestCases(initialBaselineTestCases)
    )
      ? normalizeArtifactPayloadToCasesBundle(initialBaselineTestCases).cases
      : initialCasesBundle.cases;
    setTestCaseStructure(initialCasesBundle.structure);
    if (initialCasesBundle.structure === 'categorized') {
      setImportTargetCategory('positive');
    }
    setCategorizedTestCases(initialCasesBundle.cases);
    setBaselineTestCases(cloneCases(baselineSource));
    setManualImportMeta(normalizeManualImportMeta(processingResult?.manual_import));
    setSelectedCaseKeys([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBaselineTestCases]);

  const getCaseId = (testCase) => String(testCase?.test_case_no || '').trim();
  const authoritativeInputSourceTypes = new Set([
    'manual_upload',
    'manual_uploaded',
    'user_imported',
    'testcase_excel',
    'test_case_excel',
    'excel_test_cases',
  ]);
  const inputSetHasRows = (inputSet) => {
    const rows = inputSet?.user_inputs || inputSet?.inputs || inputSet?.values;
    return Array.isArray(rows) && rows.some((item) => (
      item && typeof item === 'object' && String(item.field || item.name || item.label || '').trim()
    ));
  };
  const hasAuthoritativeInputSets = (testCase = {}) => {
    const inputSets = Array.isArray(testCase?.user_input_sets) ? testCase.user_input_sets : [];
    if (!inputSets.some(inputSetHasRows)) return false;
    const source = testCase?.source && typeof testCase.source === 'object' ? testCase.source : {};
    const sourceType = String(testCase?.source_type || source.source_type || '').trim().toLowerCase();
    if (authoritativeInputSourceTypes.has(sourceType)) return true;
    if (String(testCase?.source_import_id || source.source_import_id || source.import_id || '').trim()) return true;
    if (String(testCase?.input_values_updated_at || '').trim()) return true;
    return inputSets.some((item) => String(item?.set_name || '').trim().toLowerCase() === 'imported test data');
  };
  const mergeInputDataForCase = (currentCase, enrichedCase, replaceExisting = false) => {
    if (!currentCase || !enrichedCase) return currentCase;
    const enrichedSets = Array.isArray(enrichedCase.user_input_sets)
      ? enrichedCase.user_input_sets
      : [];
    if (enrichedSets.length === 0) return currentCase;

    const currentSets = Array.isArray(currentCase.user_input_sets)
      ? currentCase.user_input_sets
      : [];
    if (currentSets.length > 0 && hasAuthoritativeInputSets(currentCase)) {
      return currentCase;
    }
    if (replaceExisting || currentSets.length === 0) {
      return normalizeUserInputSetsForCase({
        ...currentCase,
        user_inputs: Array.isArray(enrichedCase.user_inputs)
          ? enrichedCase.user_inputs
          : currentCase.user_inputs,
        user_input_sets: enrichedSets,
      });
    }

    if (currentSets.length === 1 && enrichedSets.length > 1) {
      return normalizeUserInputSetsForCase({
        ...currentCase,
        user_input_sets: [currentSets[0], ...enrichedSets.slice(1)],
      });
    }

    return currentCase;
  };
  const mergeEnrichedCategory = (currentCases = [], enrichedCases = [], onlyUnedited = false) => {
    const enrichedById = new Map(
      (enrichedCases || [])
        .map((testCase) => [getCaseId(testCase), testCase])
        .filter(([caseId]) => Boolean(caseId))
    );
    let applied = 0;
    let skipped = 0;
    const nextCases = (currentCases || []).map((testCase) => {
      const enrichedCase = enrichedById.get(getCaseId(testCase));
      if (!enrichedCase) return testCase;
      if (onlyUnedited && isEditedCase(testCase)) {
        skipped += 1;
        return testCase;
      }
      const nextCase = mergeInputDataForCase(testCase, enrichedCase, !onlyUnedited);
      if (nextCase !== testCase) applied += 1;
      return nextCase;
    });
    return { nextCases, applied, skipped };
  };

  const buildLivePayloadSignature = (payload, streamStatus) => {
    const normalized = normalizeArtifactPayloadToCasesBundle(payload);
    const caseSignature = (prefix, item) => {
      const caseId = getCaseId(item);
      const inputSetCount = Array.isArray(item?.user_input_sets) ? item.user_input_sets.length : 0;
      return `${prefix}:${caseId}:${inputSetCount}`;
    };
    const ids = [
      ...(normalized.cases.positive || []).map((item) => caseSignature('p', item)),
      ...(normalized.cases.negative || []).map((item) => caseSignature('n', item)),
      ...(normalized.cases.all || []).map((item) => caseSignature('a', item)),
    ];
    return `${streamStatus || 'live'}::${ids.join('|')}`;
  };

  const mergeLiveGeneratedCategory = (currentCases = [], incomingCases = [], replaceExisting = false) => {
    const nextCases = [...(currentCases || [])];
    const indexById = new Map(
      nextCases
        .map((testCase, index) => [getCaseId(testCase), index])
        .filter(([caseId]) => Boolean(caseId))
    );

    (incomingCases || []).forEach((incomingCase) => {
      const caseId = getCaseId(incomingCase);
      if (!caseId || !indexById.has(caseId)) {
        nextCases.push(incomingCase);
        if (caseId) indexById.set(caseId, nextCases.length - 1);
        return;
      }

      const existingIndex = indexById.get(caseId);
      const existingCase = nextCases[existingIndex];
      if (replaceExisting && !isEditedCase(existingCase)) {
        nextCases[existingIndex] = incomingCase;
        return;
      }

      const mergedCase = mergeInputDataForCase(existingCase, incomingCase, false);
      if (mergedCase !== existingCase) {
        nextCases[existingIndex] = mergedCase;
      }
    });
    return nextCases;
  };

  const applyLiveGeneratedPayload = useCallback((payload, streamStatus) => {
    if (!payload || typeof payload !== 'object') return;
    const signature = buildLivePayloadSignature(payload, streamStatus);
    if (!signature || appliedStreamSignatureRef.current === signature) return;

    const normalized = normalizeArtifactPayloadToCasesBundle(payload);
    const shouldReplace = streamStatus === 'completed' && !hasChangesRef.current;
    if (shouldReplace) {
      setTestCaseStructure(normalized.structure);
      setCategorizedTestCases(normalized.cases);
      setBaselineTestCases(cloneCases(normalized.cases));
      setHasChanges(false);
      appliedStreamSignatureRef.current = signature;
      return;
    }

    setCategorizedTestCases((prev) => {
      if (normalized.structure === 'categorized') {
        return {
          ...prev,
          positive: mergeLiveGeneratedCategory(prev.positive, normalized.cases.positive, false),
          negative: mergeLiveGeneratedCategory(prev.negative, normalized.cases.negative, false),
        };
      }
      return {
        ...prev,
        all: mergeLiveGeneratedCategory(prev.all, normalized.cases.all, false),
      };
    });

    if (!hasChangesRef.current) {
      setBaselineTestCases((prev) => {
        if (normalized.structure === 'categorized') {
          return {
            ...prev,
            positive: mergeLiveGeneratedCategory(prev.positive, normalized.cases.positive, true),
            negative: mergeLiveGeneratedCategory(prev.negative, normalized.cases.negative, true),
          };
        }
        return {
          ...prev,
          all: mergeLiveGeneratedCategory(prev.all, normalized.cases.all, true),
        };
      });
    }

    appliedStreamSignatureRef.current = signature;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditedCase]);

  const applyEnrichedArtifactPayload = useCallback((payload, meta, artifactSignature) => {
    if (!payload || typeof payload !== 'object') return;
    if (appliedEnrichmentRef.current === artifactSignature) return;

    const normalized = normalizeArtifactPayloadToCasesBundle(payload);
    const onlyUnedited = hasChangesRef.current;
    let totalApplied = 0;
    let totalSkipped = 0;

    if (testCaseStructure === 'categorized') {
      const positiveMerge = mergeEnrichedCategory(
        categorizedTestCases.positive,
        normalized.cases.positive,
        onlyUnedited
      );
      const negativeMerge = mergeEnrichedCategory(
        categorizedTestCases.negative,
        normalized.cases.negative,
        onlyUnedited
      );
      totalApplied = positiveMerge.applied + negativeMerge.applied;
      totalSkipped = positiveMerge.skipped + negativeMerge.skipped;
      const nextState = {
        ...categorizedTestCases,
        positive: positiveMerge.nextCases,
        negative: negativeMerge.nextCases,
      };
      setCategorizedTestCases(nextState);
      if (!onlyUnedited) {
        setBaselineTestCases(cloneCases(nextState));
        setHasChanges(false);
      } else {
        setBaselineTestCases((prev) => ({
          ...prev,
          positive: mergeEnrichedCategory(prev.positive, normalized.cases.positive, false).nextCases,
          negative: mergeEnrichedCategory(prev.negative, normalized.cases.negative, false).nextCases,
        }));
      }
    } else {
      const flatMerge = mergeEnrichedCategory(categorizedTestCases.all, normalized.cases.all, onlyUnedited);
      totalApplied = flatMerge.applied;
      totalSkipped = flatMerge.skipped;
      const nextState = {
        ...categorizedTestCases,
        all: flatMerge.nextCases,
      };
      setCategorizedTestCases(nextState);
      if (!onlyUnedited) {
        setBaselineTestCases(cloneCases(nextState));
        setHasChanges(false);
      } else {
        setBaselineTestCases((prev) => ({
          ...prev,
          all: mergeEnrichedCategory(prev.all, normalized.cases.all, false).nextCases,
        }));
      }
    }

    appliedEnrichmentRef.current = artifactSignature;
    if (meta?.suggestedAdditional) {
      const suggestedPositive = Number(meta.suggestedAdditional.positive_count || 0);
      const suggestedNegative = Number(meta.suggestedAdditional.negative_count || 0);
      if (Number.isFinite(suggestedPositive)) setMorePositive(clampAdditionalCount(suggestedPositive));
      if (Number.isFinite(suggestedNegative)) setMoreNegative(clampAdditionalCount(suggestedNegative));
    }
    if (totalApplied > 0) {
      setEnrichmentNotice(
        totalSkipped > 0
          ? 'Input data is ready. Unedited cases were updated; edited cases were left untouched.'
          : 'Input data is ready and has been added to the test cases.'
      );
    } else if (totalSkipped > 0) {
      setEnrichmentNotice('Input data is ready. Edited cases were left untouched to protect your changes.');
    } else {
      setEnrichmentNotice('Input data enrichment completed.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    categorizedTestCases,
    testCaseStructure,
    isEditedCase,
  ]);

  useEffect(() => {
    setEnrichmentMeta(extractEnrichmentMeta(processingResult));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    processingResult?.enrichment_status,
    processingResult?.input_sets_status,
    processingResult?.gap_analysis_status,
    processingResult?.enriched_at,
  ]);

  useEffect(() => {
    const streamStatus = String(processingResult?.stream_status || '').trim().toLowerCase();
    if (!['running', 'completed', 'failed'].includes(streamStatus)) return;
    applyLiveGeneratedPayload(processingResult?.test_cases, streamStatus);
  }, [
    applyLiveGeneratedPayload,
    processingResult?.stream_status,
    processingResult?.test_cases,
  ]);

  useEffect(() => {
    const streamStatus = String(processingResult?.stream_status || '').trim().toLowerCase();
    if (streamStatus) return;

    const normalized = normalizeArtifactPayloadToCasesBundle(processingResult?.test_cases);
    const incomingCount =
      (normalized.cases.positive?.length || 0)
      + (normalized.cases.negative?.length || 0)
      + (normalized.cases.all?.length || 0);
    const currentCount =
      (categorizedTestCases.positive?.length || 0)
      + (categorizedTestCases.negative?.length || 0)
      + (categorizedTestCases.all?.length || 0);

    if (incomingCount === 0 || currentCount > 0 || hasChangesRef.current) return;

    setTestCaseStructure(normalized.structure);
    setCategorizedTestCases(normalized.cases);
    setBaselineTestCases(cloneCases(normalized.cases));
    setHasChanges(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    processingResult?.artifact?.version,
    processingResult?.test_cases,
    categorizedTestCases.positive?.length,
    categorizedTestCases.negative?.length,
    categorizedTestCases.all?.length,
  ]);

  useEffect(() => {
    requestedArtifactVersionRef.current = null;
  }, [pdfId, processingResult?.section_number, requestedArtifactVersion]);

  useEffect(() => {
    const sectionNumber = processingResult?.section_number || selectedSection;
    const statusValue = enrichmentMeta.status;
    if (!pdfId || !sectionNumber || !['pending', 'queued', 'running'].includes(statusValue)) {
      return undefined;
    }

    let cancelled = false;
    let inFlight = false;
    const poll = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        const response = await apiFetch(API_ENDPOINTS.SECTION_LATEST_ARTIFACTS(pdfId, sectionNumber));
        if (!response.ok) return;
        const payload = await response.json().catch(() => ({}));
        if (cancelled) return;
        const activeArtifact = payload?.test_cases?.active || null;
        if (!activeArtifact) return;
        const nextMeta = extractEnrichmentMeta(activeArtifact);
        setEnrichmentMeta(nextMeta);
        const nextStatus = nextMeta.status;
        if (nextStatus === 'completed') {
          const signature = [
            activeArtifact?.id || 'artifact',
            activeArtifact?.version || 'v',
            activeArtifact?.updated_at || nextMeta.enrichedAt || 'done',
          ].join(':');
          applyEnrichedArtifactPayload(activeArtifact.payload, nextMeta, signature);
        }
      } catch (_err) {
        // Background enrichment polling is best-effort; core test cases remain editable.
      } finally {
        inFlight = false;
      }
    };

    poll();
    const timerId = window.setInterval(poll, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(timerId);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    applyEnrichedArtifactPayload,
    enrichmentMeta.status,
    pdfId,
    processingResult?.section_number,
    selectedSection,
  ]);

  const applyArtifactVersionToEditor = useCallback((artifact, options = {}) => {
    const payload = artifact?.payload;
    const normalized = normalizeArtifactPayloadToCasesBundle(payload);
    const shouldActivate = Boolean(options?.markAsSaved);

    setTestCaseStructure(normalized.structure);
    if (normalized.structure === 'categorized') {
      setImportTargetCategory('positive');
    }
    setCategorizedTestCases(normalized.cases);
    setBaselineTestCases(cloneCases(normalized.cases));
    setManualImportMeta(normalizeManualImportMeta(artifact?.request_payload?.manual_import));
    setSelectedCaseKeys([]);
    setHasChanges(false);
    setSaveError(null);
    setLoadedVersion(artifact?.version ?? null);
    if (shouldActivate) {
      setLastSavedVersion(artifact?.version ?? null);
      setLastSavedAt(artifact?.updated_at || null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadVersionHistory = useCallback(async () => {
    if (!versioningEnabled) {
      setVersionHistory([]);
      setActiveVersion(null);
      setIsLoadingVersions(false);
      setVersionError(null);
      return;
    }
    if (!pdfId || !processingResult?.section_number) return;

    setIsLoadingVersions(true);
    setVersionError(null);
    try {
      const params = new URLSearchParams({
        artifact_type: 'test_cases',
        artifact_kind: 'section',
      });
      const response = await apiFetch(
        `${API_ENDPOINTS.SECTION_ARTIFACTS(pdfId, processingResult.section_number)}?${params.toString()}`
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to load test-case versions');
      }

      const payload = await response.json();
      const versions = Array.isArray(payload?.versions) ? payload.versions : [];
      const resolvedActiveVersion = payload?.active_version ?? null;
      const requestedVersion = Number(requestedArtifactVersion);
      const activeArtifact =
        (
          Number.isFinite(requestedVersion) && requestedVersion > 0
            ? versions.find((artifact) => Number(artifact?.version) === requestedVersion)
            : null
        )
        ||
        versions.find((artifact) => artifact?.version === resolvedActiveVersion)
        || versions[0]
        || null;

      setVersionHistory(versions);
      setActiveVersion(resolvedActiveVersion);
      setLoadedVersion(activeArtifact?.version ?? resolvedActiveVersion ?? null);
      if (activeArtifact) {
        setLastSavedVersion(activeArtifact?.version ?? null);
        setLastSavedAt(activeArtifact?.updated_at || null);
        if (
          Number.isFinite(requestedVersion)
          && requestedVersion > 0
          && requestedArtifactVersionRef.current !== requestedVersion
        ) {
          requestedArtifactVersionRef.current = requestedVersion;
          applyArtifactVersionToEditor(activeArtifact, { markAsSaved: false });
          setIsVersionPanelOpen(true);
        }
      }
    } catch (err) {
      setVersionError(err.message || 'Failed to load test-case versions');
    } finally {
      setIsLoadingVersions(false);
    }
  }, [applyArtifactVersionToEditor, pdfId, processingResult?.section_number, requestedArtifactVersion, versioningEnabled]);

  const activateArtifactVersion = async (version) => {
    if (!pdfId || !processingResult?.section_number || !version) return;
    if (version === activeVersion) return;
    if (hasChanges) {
      const shouldContinue = window.confirm(
        'Switching versions will replace your current unsaved edits in the editor. Continue?'
      );
      if (!shouldContinue) return;
    }

    setIsSaving(true);
    setSaveError(null);
    try {
      const response = await apiFetch(
        API_ENDPOINTS.ACTIVATE_SECTION_ARTIFACT(pdfId, processingResult.section_number),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            version,
            artifact_type: 'test_cases',
            artifact_kind: 'section',
          }),
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to activate test-case version');
      }

      const payload = await response.json();
      applyArtifactVersionToEditor(payload?.artifact, { markAsSaved: true });
      await loadVersionHistory();
    } catch (err) {
      setSaveError(err.message || 'Failed to activate version');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteArtifactVersion = async (artifact) => {
    const version = artifact?.version;
    if (!pdfId || !processingResult?.section_number || !version) return;

    const isCurrentVersion = version === activeVersion;
    const confirmationMessage = isCurrentVersion
      ? 'Delete this active version?\n\nIf another version exists, it will become the active editable version.'
      : `Delete v${version}? This cannot be undone.`;
    const shouldDelete = window.confirm(confirmationMessage);
    if (!shouldDelete) return;

    if (isCurrentVersion && hasChanges) {
      const shouldDiscard = window.confirm(
        'Deleting the active version will replace your current unsaved edits in the editor. Continue?'
      );
      if (!shouldDiscard) return;
    }

    setIsSaving(true);
    setSaveError(null);
    try {
      const response = await apiFetch(
        API_ENDPOINTS.DELETE_SECTION_ARTIFACT(pdfId, processingResult.section_number),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            version,
            artifact_type: 'test_cases',
            artifact_kind: 'section',
          }),
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete test-case version');
      }

      const payload = await response.json();
      if (payload?.active_artifact) {
        applyArtifactVersionToEditor(payload.active_artifact, { markAsSaved: true });
      } else if (isCurrentVersion) {
        setActiveVersion(null);
        setLoadedVersion(null);
        setLastSavedVersion(null);
        setLastSavedAt(null);
      }
      await loadVersionHistory();
    } catch (err) {
      setSaveError(err.message || 'Failed to delete version');
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!onEditComplete) return;
    const payload =
      testCaseStructure === 'flat'
        ? categorizedTestCases.all
        : categorizedTestCases;
    onEditComplete(payload);
  }, [categorizedTestCases, testCaseStructure, onEditComplete]);

  useEffect(() => {
    loadVersionHistory();
    setLastSavedVersion(
      processingResult?.artifact?.version
      ?? null
    );
    setLoadedVersion(
      processingResult?.artifact?.version
      ?? null
    );
  }, [
    loadVersionHistory,
    processingResult?.artifact?.version,
  ]);

  useEffect(() => {
    if (!isGenerateMoreModalOpen && !pendingRemoveCase) return undefined;

    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;

      if (pendingRemoveCase) {
        setPendingRemoveCase(null);
        return;
      }

      if (status !== 'processing') {
        setIsGenerateMoreModalOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isGenerateMoreModalOpen, isSaving, pendingRemoveCase, status]);

  const openGenerateMoreModal = () => {
    setGenerateMoreError(null);
    setGenerateMoreStatus(null);
    setIsGenerateMoreModalOpen(true);
  };

  const closeGenerateMoreModal = () => {
    if (status === 'processing') return;
    setIsGenerateMoreModalOpen(false);
  };

  const closeRemoveCaseModal = () => {
    if (isSaving) return;
    setPendingRemoveCase(null);
  };

  const closeSaveOptionsModal = () => {
    if (isSaving) return;
    setIsSaveOptionsOpen(false);
  };

  const toggleInputSetsPanel = (caseKey) => {
    setExpandedInputSetsByCase((prev) => ({
      ...prev,
      [caseKey]: !prev[caseKey],
    }));
  };

  const handleFieldChange = (category, caseIndex, field, value) => {
    setCategorizedTestCases((prev) => {
      const updated = { ...prev };
      const casesArray = [...updated[category]];
      const caseItem = { ...(casesArray[caseIndex] || {}) };

      if (field === 'test_steps') {
        caseItem.test_steps = Array.isArray(value)
          ? value
          : value.split('\n');
      } else if (field === 'preconditions') {
        caseItem.preconditions = Array.isArray(value)
          ? value
          : value.split('\n');
      } else if (field === 'expected_result') {
        caseItem.expected_result = value;
        caseItem.expected_results = Array.isArray(value)
          ? value
          : String(value || '').split('\n');
      } else if (field === 'user_role' || field === 'User_Role') {
        caseItem.user_role = value;
        caseItem.User_Role = value;
      } else if (field === 'module' || field === 'Module') {
        caseItem.module = value;
        caseItem.Module = value;
      } else if (field === 'user_inputs') {
        const normalizedInputs = normalizeUserInputList(Array.isArray(value) ? value : []);
        const currentSets = Array.isArray(caseItem.user_input_sets) ? [...caseItem.user_input_sets] : [];
        const activeIndex = clampInputSetIndex(
          caseItem.active_input_set_index ?? 0,
          currentSets.length || 1
        );

        if (currentSets.length === 0 && normalizedInputs.length > 0) {
          currentSets.push({
            set_name: 'Set 1',
            set_type: 'primary',
            user_inputs: normalizedInputs,
          });
        } else if (currentSets[activeIndex]) {
          currentSets[activeIndex] = {
            ...currentSets[activeIndex],
            user_inputs: normalizedInputs,
          };
        }

        caseItem.user_input_sets = currentSets;
        caseItem.active_input_set_index = activeIndex;
        caseItem.user_inputs = normalizedInputs;
      } else {
        caseItem[field] = value;
      }

      casesArray[caseIndex] = normalizeUserInputSetsForCase(caseItem);
      updated[category] = casesArray;
      return updated;
    });
    setHasChanges(true);
    setSaveError(null);
  };

  const updateCaseInputSets = (category, caseIndex, updater) => {
    setCategorizedTestCases((prev) => {
      const updated = { ...prev };
      const casesArray = [...(updated[category] || [])];
      const originalCase = { ...(casesArray[caseIndex] || {}) };
      const normalizedCase = normalizeUserInputSetsForCase(originalCase);
      const nextCase = updater({
        ...normalizedCase,
        user_input_sets: Array.isArray(normalizedCase.user_input_sets)
          ? [...normalizedCase.user_input_sets]
          : [],
      });
      casesArray[caseIndex] = normalizeUserInputSetsForCase(nextCase || normalizedCase);
      updated[category] = casesArray;
      return updated;
    });
    setHasChanges(true);
    setSaveError(null);
  };

  const handleActiveInputSetChange = (category, caseIndex, nextIndex) => {
    updateCaseInputSets(category, caseIndex, (testCase) => {
      const sets = Array.isArray(testCase.user_input_sets) ? [...testCase.user_input_sets] : [];
      const activeIndex = clampInputSetIndex(nextIndex, sets.length || 1);
      return {
        ...testCase,
        user_input_sets: sets,
        active_input_set_index: activeIndex,
      };
    });
  };

  const addInputSet = (category, caseIndex) => {
    updateCaseInputSets(category, caseIndex, (testCase) => {
      const sets = Array.isArray(testCase.user_input_sets) ? [...testCase.user_input_sets] : [];
      if (sets.length >= MAX_INPUT_SETS_PER_CASE) {
        return testCase;
      }
      const activeIndex = clampInputSetIndex(testCase.active_input_set_index ?? 0, sets.length || 1);
      const templateRows = buildEmptyInputTemplateFromSets(sets, activeIndex);
      const nextIndex = sets.length;
      sets.push({
        set_name: `Set ${nextIndex + 1}`,
        set_type: '',
        user_inputs: templateRows,
      });
      return {
        ...testCase,
        user_input_sets: sets,
        active_input_set_index: nextIndex,
      };
    });
  };

  const removeInputSet = (category, caseIndex, setIndex) => {
    updateCaseInputSets(category, caseIndex, (testCase) => {
      const sets = Array.isArray(testCase.user_input_sets) ? [...testCase.user_input_sets] : [];
      if (sets.length <= 1 || setIndex < 0 || setIndex >= sets.length) {
        return testCase;
      }
      sets.splice(setIndex, 1);
      return {
        ...testCase,
        user_input_sets: sets,
        active_input_set_index: clampInputSetIndex(testCase.active_input_set_index ?? 0, sets.length),
      };
    });
  };

  const addInputRowToSet = (category, caseIndex, setIndex) => {
    updateCaseInputSets(category, caseIndex, (testCase) => {
      const sets = Array.isArray(testCase.user_input_sets) ? [...testCase.user_input_sets] : [];
      const targetSet = sets[setIndex];
      if (!targetSet) return testCase;
      const nextRows = normalizeUserInputList(targetSet.user_inputs || []);
      nextRows.push({ field: '', value: '' });
      sets[setIndex] = {
        ...targetSet,
        user_inputs: nextRows,
      };
      return {
        ...testCase,
        user_input_sets: sets,
      };
    });
  };

  const removeInputRowFromSet = (category, caseIndex, setIndex, inputIndex) => {
    updateCaseInputSets(category, caseIndex, (testCase) => {
      const sets = Array.isArray(testCase.user_input_sets) ? [...testCase.user_input_sets] : [];
      const targetSet = sets[setIndex];
      if (!targetSet) return testCase;
      const nextRows = normalizeUserInputList(targetSet.user_inputs || []);
      if (inputIndex < 0 || inputIndex >= nextRows.length) return testCase;
      nextRows.splice(inputIndex, 1);
      sets[setIndex] = {
        ...targetSet,
        user_inputs: nextRows,
      };
      return {
        ...testCase,
        user_input_sets: sets,
      };
    });
  };

  const handleInputRowChange = (category, caseIndex, setIndex, inputIndex, key, value) => {
    updateCaseInputSets(category, caseIndex, (testCase) => {
      const sets = Array.isArray(testCase.user_input_sets) ? [...testCase.user_input_sets] : [];
      const targetSet = sets[setIndex];
      if (!targetSet) return testCase;
      const nextRows = normalizeUserInputList(targetSet.user_inputs || []);
      const current = { ...(nextRows[inputIndex] || { field: '', value: '' }) };
      current[key] = value;
      nextRows[inputIndex] = normalizeUserInputEntry(current);
      sets[setIndex] = {
        ...targetSet,
        user_inputs: nextRows,
      };
      return {
        ...testCase,
        user_input_sets: sets,
      };
    });
  };

  const executeSaveEdits = async () => {
    setIsSaving(true);
    setSaveError(null);

    try {
      const payload = sanitizeCasesForPersistence(
        testCaseStructure === 'flat'
          ? categorizedTestCases.all
          : categorizedTestCases
      );
      const baselinePayload = sanitizeCasesForPersistence(
        testCaseStructure === 'flat'
          ? baselineTestCases.all
          : baselineTestCases
      );
      const result = await onSaveTestCases(payload, baselinePayload, {
        autosave: false,
        manualImportMeta,
      });
      setBaselineTestCases(cloneCases(categorizedTestCases));
      setHasChanges(false);
      setLastSavedVersion(result?.artifact?.version ?? null);
      setLastSavedAt(result?.artifact?.updated_at || null);
      setLoadedVersion(result?.artifact?.version ?? null);
      await loadVersionHistory();
    } catch (err) {
      setSaveError(err.message || 'Failed to save test cases');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveEdits = async () => {
    if (isSaving || !hasChanges) return;
    await executeSaveEdits();
  };

  const handleContinueWithCurrentCases = async () => {
    if (isSaving || hasChanges) return;
    await executeSaveEdits();
  };

  const handlePersistAndContinue = async () => {
    if (isSaving || isStreamRunning || totalCases === 0) return;

    if (hasChanges) {
      await handleSaveEdits();
      return;
    }

    await handleContinueWithCurrentCases();
  };

  const parseCoveragePrompt = (prompt) =>
    String(prompt || '')
      .split('\n')
      .flatMap((line) => line.split(','))
      .map((item) => item.trim())
      .filter(Boolean);

  const clampAdditionalCount = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return 0;
    return Math.max(0, Math.min(MAX_ADDITIONAL_PER_TYPE, Math.floor(numeric)));
  };

  const updateMorePositive = (nextValue) => {
    setMorePositive(clampAdditionalCount(nextValue));
    setGenerateMoreError(null);
    setGenerateMoreStatus(null);
  };

  const updateMoreNegative = (nextValue) => {
    setMoreNegative(clampAdditionalCount(nextValue));
    setGenerateMoreError(null);
    setGenerateMoreStatus(null);
  };

  const coverageGapsSource = Array.isArray(processingResult?.coverage_gaps) && processingResult.coverage_gaps.length > 0
    ? processingResult.coverage_gaps
    : enrichmentMeta.coverageGaps;
  const coverageGaps = Array.isArray(coverageGapsSource)
    ? coverageGapsSource
      .map((gap) => String(gap || '').trim())
      .filter(Boolean)
    : [];
  const hasCoverageGaps = coverageGaps.length > 0;
  const coverageAuditStatus = normalizeEnrichmentStatus(
    processingResult?.gap_analysis_status
    || processingResult?.test_cases?.gap_analysis_status
    || enrichmentMeta.gapStatus
    || enrichmentStatus
  );
  const resolvedCoverageScore = normalizeCoverageScore(
    processingResult?.coverage_score,
    processingResult?.test_cases?.coverage_score,
    enrichmentMeta.coverageScore
  );
  const coverageScore = coverageAuditStatus === 'completed' || resolvedCoverageScore > 0
    ? resolvedCoverageScore
    : null;
  const missingAreasSource = Array.isArray(processingResult?.missing_areas) && processingResult.missing_areas.length > 0
    ? processingResult.missing_areas
    : (Array.isArray(processingResult?.test_cases?.missing_areas) && processingResult.test_cases.missing_areas.length > 0
      ? processingResult.test_cases.missing_areas
      : enrichmentMeta.missingAreas);
  const missingAreas = normalizeMissingAreas(missingAreasSource);
  const duplicateCaseIds = normalizeStringList(
    Array.isArray(processingResult?.duplicates) && processingResult.duplicates.length > 0
      ? processingResult.duplicates
      : (Array.isArray(processingResult?.test_cases?.duplicates) && processingResult.test_cases.duplicates.length > 0
        ? processingResult.test_cases.duplicates
        : enrichmentMeta.duplicates)
  );
  const weakCaseIds = normalizeStringList(
    Array.isArray(processingResult?.weak_cases) && processingResult.weak_cases.length > 0
      ? processingResult.weak_cases
      : (Array.isArray(processingResult?.test_cases?.weak_cases) && processingResult.test_cases.weak_cases.length > 0
        ? processingResult.test_cases.weak_cases
        : enrichmentMeta.weakCases)
  );
  const suggestedAdditional = (
    processingResult?.suggested_additional
    || processingResult?.test_cases?.suggested_additional
    || enrichmentMeta.suggestedAdditional
    || {}
  );
  const suggestedAdditionalPositive = clampAdditionalCount(suggestedAdditional?.positive_count || 0);
  const suggestedAdditionalNegative = clampAdditionalCount(suggestedAdditional?.negative_count || 0);
  const auditFindingCount = coverageGaps.length + missingAreas.length + duplicateCaseIds.length + weakCaseIds.length;
  const hasCoverageAudit = Boolean(
    coverageScore !== null
    || auditFindingCount > 0
    || isEnrichmentPending
    || isEnrichmentComplete
    || isEnrichmentFailed
  );
  const coverageScoreTone = coverageScore === null
    ? 'border-slate-200 bg-slate-50 text-slate-700'
    : coverageScore >= 85
      ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
      : coverageScore >= 65
        ? 'border-amber-200 bg-amber-50 text-amber-800'
        : 'border-rose-200 bg-rose-50 text-rose-800';
  const missingCoverageFocusItems = [
    ...missingAreas.map((area) => area.description),
    ...coverageGaps,
  ]
    .map((item) => String(item || '').trim())
    .filter((item, index, list) => item && list.indexOf(item) === index);
  const getMissingCoverageCounts = () => {
    const fallbackCount = missingCoverageFocusItems.length > 0 ? 1 : 0;
    const counts = missingAreas.reduce(
      (acc, area) => {
        const suggestedCount = Math.max(1, Number(area.suggested_count || 0));
        const typeText = `${area.type} ${area.description}`.toLowerCase();
        if (/\b(happy|positive|success|valid)\b/.test(typeText)) {
          acc.positive += suggestedCount;
        } else {
          acc.negative += suggestedCount;
        }
        return acc;
      },
      { positive: suggestedAdditionalPositive, negative: suggestedAdditionalNegative }
    );
    return {
      positive: clampAdditionalCount(counts.positive || fallbackCount),
      negative: clampAdditionalCount(counts.negative || fallbackCount),
    };
  };
  const missingCoverageCounts = getMissingCoverageCounts();
  const canGenerateMissingCoverage = Boolean(
    onGenerateMore
    && !isSaving
    && status !== 'processing'
    && status !== 'bulk_processing'
    && missingCoverageFocusItems.length > 0
    && (missingCoverageCounts.positive + missingCoverageCounts.negative) > 0
  );
  const autoMissingCoverageCycleKey = [
    processingResult?.pdf_id || '',
    processingResult?.section_number || '',
    processingResult?.generated_at || processingResult?.created_at || processingResult?.updated_at || processingResult?.enriched_at || enrichmentMeta.enrichedAt || '',
  ].join('|');

  const applyCoverageGapsToPrompt = () => {
    if (!hasCoverageGaps) return;
    setCoveragePrompt(coverageGaps.join('\n'));
    setGenerateMoreError(null);
    setGenerateMoreStatus(null);
  };

  const handleGenerateMoreCases = async (override = {}) => {
    if (!onGenerateMore) return;

    const additionalPositive = clampAdditionalCount(override.positive_count ?? morePositive);
    const additionalNegative = clampAdditionalCount(override.negative_count ?? moreNegative);
    const requestedFocus = Array.isArray(override.additional_focus)
      ? override.additional_focus
      : parseCoveragePrompt(coveragePrompt);
    const totalAdditional = additionalPositive + additionalNegative;

    if (totalAdditional <= 0) {
      setGenerateMoreError('Add at least one additional positive or negative test case.');
      return;
    }
    if (
      additionalPositive > MAX_ADDITIONAL_PER_TYPE ||
      additionalNegative > MAX_ADDITIONAL_PER_TYPE
    ) {
      setGenerateMoreError(
        `You can generate at most ${MAX_ADDITIONAL_PER_TYPE} positive and ${MAX_ADDITIONAL_PER_TYPE} negative test cases at a time.`
      );
      return;
    }

    setGenerateMoreError(null);
    setGenerateMoreStatus(null);
    try {
      const existingPayload = sanitizeCasesForPersistence(
        testCaseStructure === 'flat'
          ? categorizedTestCases.all
          : {
              positive: categorizedTestCases.positive,
              negative: categorizedTestCases.negative,
            }
      );

      const appendResponse = await onGenerateMore({
        positive_count: additionalPositive,
        negative_count: additionalNegative,
        additional_focus: requestedFocus,
        existing_test_cases: existingPayload,
      });

      if (!appendResponse || !appendResponse.test_cases) {
        return;
      }

      const appendedFlatCount = Array.isArray(appendResponse.test_cases)
        ? appendResponse.test_cases.length
        : 0;
      const appendedPositiveCount = appendedFlatCount > 0
        ? 0
        : toArray(appendResponse.test_cases?.positive).length;
      const appendedNegativeCount = appendedFlatCount > 0
        ? 0
        : toArray(appendResponse.test_cases?.negative).length;
      const appendedSummary = appendedFlatCount > 0
        ? `${appendedFlatCount} AI test case${appendedFlatCount === 1 ? '' : 's'}`
        : `${appendedPositiveCount} positive and ${appendedNegativeCount} negative AI test cases`;

      const refreshedAudit = extractEnrichmentMeta(appendResponse);
      if (
        refreshedAudit.coverageScore !== null
        || refreshedAudit.coverageGaps.length > 0
        || refreshedAudit.missingAreas.length > 0
        || refreshedAudit.duplicates.length > 0
        || refreshedAudit.weakCases.length > 0
        || refreshedAudit.suggestedAdditional
      ) {
        setEnrichmentMeta(refreshedAudit);
      }

      setCategorizedTestCases((prev) => {
        const usedIds = collectUsedCaseIds(prev);
        const nextState = cloneCases(prev);

        if (testCaseStructure === 'flat') {
          const generatedFlat = Array.isArray(appendResponse.test_cases)
            ? appendResponse.test_cases
            : [
              ...toArray(appendResponse.test_cases?.positive),
              ...toArray(appendResponse.test_cases?.negative),
            ];

          const normalized = generatedFlat.map((testCase, idx) =>
            normalizeGeneratedAdditionalCase(testCase, idx, 'AC-TC')
          );
          const unique = assignUniqueCaseIds(normalized, usedIds, 'AC-TC', 'ADD');
          nextState.all = [...prev.all, ...unique.assigned];
          return nextState;
        }

        const generatedPositive = toArray(appendResponse.test_cases?.positive);
        const generatedNegative = toArray(appendResponse.test_cases?.negative);

        const normalizedPositive = generatedPositive.map((testCase, idx) =>
          normalizeGeneratedAdditionalCase(testCase, idx, 'AC-TC-POS')
        );
        const normalizedNegative = generatedNegative.map((testCase, idx) =>
          normalizeGeneratedAdditionalCase(testCase, idx, 'AC-TC-NEG')
        );

        const uniquePositive = assignUniqueCaseIds(normalizedPositive, usedIds, 'AC-TC-POS', 'ADD');
        const uniqueNegative = assignUniqueCaseIds(normalizedNegative, usedIds, 'AC-TC-NEG', 'ADD');

        nextState.positive = [...prev.positive, ...uniquePositive.assigned];
        nextState.negative = [...prev.negative, ...uniqueNegative.assigned];
        return nextState;
      });

      setCoveragePrompt('');
      setHasChanges(true);
      setGenerateMoreStatus(`Added ${appendedSummary}.`);
      setIsGenerateMoreModalOpen(false);
    } catch (err) {
      setGenerateMoreStatus(null);
      setGenerateMoreError(err.message || 'Failed to generate additional test cases');
    }
  };

  const handleGenerateMissingCoverage = () => {
    if (!canGenerateMissingCoverage) return;
    setMorePositive(missingCoverageCounts.positive);
    setMoreNegative(missingCoverageCounts.negative);
    setCoveragePrompt(missingCoverageFocusItems.join('\n'));
    setGenerateMoreError(null);
    setGenerateMoreStatus(null);
    handleGenerateMoreCases({
      positive_count: missingCoverageCounts.positive,
      negative_count: missingCoverageCounts.negative,
      additional_focus: missingCoverageFocusItems,
    });
  };

  useEffect(() => {
    if (!canGenerateMissingCoverage) return;
    if (coverageAuditStatus !== 'completed') return;
    if (!autoMissingCoverageCycleKey) return;
    if (autoMissingCoverageSignatureRef.current === autoMissingCoverageCycleKey) return;

    autoMissingCoverageSignatureRef.current = autoMissingCoverageCycleKey;
    setGenerateMoreStatus('Coverage audit found gaps. Generating missing coverage automatically...');
    handleGenerateMissingCoverage();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoMissingCoverageCycleKey, canGenerateMissingCoverage, coverageAuditStatus]);

  const normalizeImportedCase = (testCase, index, prefix = 'MANUAL') => {
    const safeCase = testCase && typeof testCase === 'object' && !Array.isArray(testCase) ? testCase : {};
    const steps = safeCase.test_steps;
    const normalizedCase = {
      ...safeCase,
      test_case_no: resolveCaseIdForPrefix(safeCase.test_case_no, prefix, index),
      test_description: safeCase.test_description || safeCase.description || '',
      test_steps: Array.isArray(steps)
        ? steps.filter((step) => String(step || '').trim())
        : String(steps || '')
          .split('\n')
          .map((step) => step.trim())
          .filter(Boolean),
      expected_result:
        safeCase.expected_result ||
        safeCase.expected_results ||
        '',
      source_type: 'manual_upload',
      _source: 'manual',
    };
    return normalizeUserInputSetsForCase(normalizedCase);
  };

  const normalizeGeneratedAdditionalCase = (testCase, index, prefix = 'AC-TC') => {
    const safeCase = testCase && typeof testCase === 'object' && !Array.isArray(testCase) ? testCase : {};
    const steps = safeCase.test_steps;
    const normalizedCase = {
      ...safeCase,
      test_case_no: resolveCaseIdForPrefix(safeCase.test_case_no, prefix, index),
      test_description: safeCase.test_description || safeCase.description || '',
      test_steps: Array.isArray(steps)
        ? steps.filter((step) => String(step || '').trim())
        : String(steps || '')
          .split('\n')
          .map((step) => step.trim())
          .filter(Boolean),
      expected_result:
        safeCase.expected_result ||
        safeCase.expected_results ||
        '',
      source_type: 'ai_generated',
      _source: 'ai',
    };
    return normalizeUserInputSetsForCase(normalizedCase);
  };

  const toArray = (items) => {
    if (Array.isArray(items)) return items;
    if (items && typeof items === 'object') {
      const values = Object.values(items);
      const allValuesObjects = values.length > 0 && values.every((value) => value && typeof value === 'object');
      return allValuesObjects ? values : [items];
    }
    return [];
  };

  const parseExcelTestCases = async (file) => {
    const fileB64 = await readFileAsBase64(file);

    const response = await apiFetch(API_ENDPOINTS.IMPORT_MANUAL_TEST_CASES_SECURE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        filename: file.name,
        file_b64: fileB64,
      }),
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload.detail || 'Failed to parse Excel file');
    }
    return response.json();
  };

  const collectUsedCaseIds = (casesState) => {
    const allCases = [
      ...(casesState?.positive || []),
      ...(casesState?.negative || []),
      ...(casesState?.all || []),
    ];

    return new Set(
      allCases
        .map((testCase) => String(testCase?.test_case_no || '').trim())
        .filter(Boolean)
    );
  };

  const assignUniqueCaseIds = (cases, usedIds, defaultPrefix, collisionTag = 'MANUAL') => {
    const renameMap = [];
    const assigned = (cases || []).map((testCase, idx) => {
      const originalId = String(testCase?.test_case_no || '').trim();
      const baseId = originalId || `${defaultPrefix}-${idx + 1}`;
      let candidate = baseId;
      let suffix = 1;

      while (usedIds.has(candidate)) {
        candidate = `${baseId}-${collisionTag}-${suffix}`;
        suffix += 1;
      }

      usedIds.add(candidate);

      if (candidate !== baseId) {
        renameMap.push({ from: baseId, to: candidate });
      }

      return {
        ...testCase,
        test_case_no: candidate,
        _renamed_from: candidate !== baseId ? baseId : testCase?._renamed_from,
      };
    });

    return { assigned, renameMap };
  };

  const mergeImportedCasesIntoState = (state, imported) => {
    const usedIds = collectUsedCaseIds(state);
    const baseCount =
      state.positive.length +
      state.negative.length +
      state.all.length;

    const result = {
      nextState: cloneCases(state),
      addedPositive: [],
      addedNegative: [],
      addedAll: [],
      renamedCount: 0,
    };

    if (testCaseStructure === 'flat') {
      const incomingFlat = imported.structure === 'categorized'
        ? [...imported.positive, ...imported.negative]
        : imported.all;

      const normalized = incomingFlat.map((testCase, idx) =>
        normalizeImportedCase(testCase, baseCount + idx, 'MANUAL')
      );
      const uniqueManual = assignUniqueCaseIds(normalized, usedIds, 'MANUAL');
      result.renamedCount += uniqueManual.renameMap.length;
      result.addedAll = uniqueManual.assigned;
      result.nextState.all = [...state.all, ...uniqueManual.assigned];
      return result;
    }

    if (imported.structure === 'categorized') {
      const normalizedPositive = imported.positive.map((testCase, idx) =>
        normalizeImportedCase(testCase, state.positive.length + idx, 'MANUAL-POS')
      );
      const normalizedNegative = imported.negative.map((testCase, idx) =>
        normalizeImportedCase(testCase, state.negative.length + idx, 'MANUAL-NEG')
      );
      const uniquePositive = assignUniqueCaseIds(normalizedPositive, usedIds, 'MANUAL-POS');
      const uniqueNegative = assignUniqueCaseIds(normalizedNegative, usedIds, 'MANUAL-NEG');
      result.renamedCount += uniquePositive.renameMap.length + uniqueNegative.renameMap.length;
      result.addedPositive = uniquePositive.assigned;
      result.addedNegative = uniqueNegative.assigned;
      result.nextState.positive = [...state.positive, ...uniquePositive.assigned];
      result.nextState.negative = [...state.negative, ...uniqueNegative.assigned];
      return result;
    }

    const normalized = imported.all.map((testCase, idx) =>
      normalizeImportedCase(
        testCase,
        state[importTargetCategory].length + idx,
        `MANUAL-${importTargetCategory.toUpperCase()}`
      )
    );
    const uniqueManual = assignUniqueCaseIds(
      normalized,
      usedIds,
      `MANUAL-${importTargetCategory.toUpperCase()}`
    );
    result.renamedCount += uniqueManual.renameMap.length;
    if (importTargetCategory === 'positive') {
      result.addedPositive = uniqueManual.assigned;
    } else {
      result.addedNegative = uniqueManual.assigned;
    }
    result.nextState[importTargetCategory] = [
      ...state[importTargetCategory],
      ...uniqueManual.assigned,
    ];
    return result;
  };

  const buildManualCaseSelectionKey = (category, testCase, fallbackIndex) => {
    const caseId = String(testCase?.test_case_no || `idx-${fallbackIndex}`).trim();
    return `${category}::${caseId}`;
  };

  const getAllCaseEntries = (casesState = categorizedTestCases) => {
    if (testCaseStructure === 'categorized') {
      return [
        ...(casesState.positive || []).map((testCase, index) => ({ category: 'positive', index, testCase })),
        ...(casesState.negative || []).map((testCase, index) => ({ category: 'negative', index, testCase })),
      ];
    }
    return (casesState.all || []).map((testCase, index) => ({ category: 'all', index, testCase }));
  };

  const isUserImportedCase = (testCase = {}) => {
    const sourceType = String(testCase?.source_type || '').trim().toLowerCase();
    return isUserImportedSourceType(sourceType)
      || testCase?._source === 'manual'
      || testCase?._source === 'excel';
  };

  const buildSelectionKeyFromEntry = (entry = {}) => {
    const explicitKey = String(entry?.selectionKey || '').trim();
    if (explicitKey) return explicitKey;
    const category = String(entry?.category || '').trim();
    if (!category) return '';
    const fallbackIndexRaw = entry?.caseIndex ?? entry?.index;
    const fallbackIndex = Number.isFinite(Number(fallbackIndexRaw))
      ? Number(fallbackIndexRaw)
      : 0;
    return buildManualCaseSelectionKey(category, entry?.testCase, fallbackIndex);
  };

  const getAiDeletionGuardError = (
    casesState = categorizedTestCases,
    removeEntries = []
  ) => {
    if (testCaseStructure !== 'categorized') return null;
    const removeKeys = new Set(
      (Array.isArray(removeEntries) ? removeEntries : [])
        .map((entry) => buildSelectionKeyFromEntry(entry))
        .filter(Boolean)
    );
    if (removeKeys.size === 0) return null;

    const initialAiPositive = (casesState.positive || []).filter(
      (testCase) => !isUserImportedCase(testCase)
    ).length;
    const initialAiNegative = (casesState.negative || []).filter(
      (testCase) => !isUserImportedCase(testCase)
    ).length;
    if (initialAiPositive + initialAiNegative === 0) return null;

    const remainingAiPositive = (casesState.positive || []).filter((testCase, index) => {
      if (isUserImportedCase(testCase)) return false;
      const selectionKey = buildManualCaseSelectionKey('positive', testCase, index);
      return !removeKeys.has(selectionKey);
    }).length;
    const remainingAiNegative = (casesState.negative || []).filter((testCase, index) => {
      if (isUserImportedCase(testCase)) return false;
      const selectionKey = buildManualCaseSelectionKey('negative', testCase, index);
      return !removeKeys.has(selectionKey);
    }).length;

    if (remainingAiPositive < 1 && remainingAiNegative < 1) {
      return 'At least one AI positive and one AI negative test case must remain.';
    }
    if (remainingAiPositive < 1) {
      return 'At least one AI positive test case must remain.';
    }
    if (remainingAiNegative < 1) {
      return 'At least one AI negative test case must remain.';
    }
    return null;
  };

  const getManualCaseEntries = (casesState = categorizedTestCases) => {
    if (testCaseStructure === 'categorized') {
      return [
        ...(casesState.positive || []).map((testCase, index) => ({ category: 'positive', index, testCase })),
        ...(casesState.negative || []).map((testCase, index) => ({ category: 'negative', index, testCase })),
      ].filter(({ testCase }) => testCase?._source === 'manual');
    }

    return (casesState.all || [])
      .map((testCase, index) => ({ category: 'all', index, testCase }))
      .filter(({ testCase }) => testCase?._source === 'manual');
  };
  const isManualCaseAlreadyNormalized = (testCase = {}) => (
    String(testCase?.normalization_status || '').trim().toLowerCase() === 'normalized'
  );
  const getPendingManualCaseEntries = (casesState = categorizedTestCases) => (
    getManualCaseEntries(casesState).filter(
      ({ testCase }) => !isManualCaseAlreadyNormalized(testCase)
    )
  );

  const toNormalizedStepList = (steps) => (
    Array.isArray(steps)
      ? steps
      : String(steps || '')
        .split('\n')
  )
    .map((step) => String(step || '').trim())
    .filter(Boolean);

  const mergeNormalizedManualCase = (originalCase, normalizedCase, normalizationMeta = {}) => {
    const safeOriginal = originalCase && typeof originalCase === 'object' ? originalCase : {};
    const safeNormalized = normalizedCase && typeof normalizedCase === 'object' ? normalizedCase : {};
    const normalizedSteps = toNormalizedStepList(safeNormalized.test_steps);
    const normalizedAt = String(
      normalizationMeta?.normalized_at
      || safeOriginal.normalized_at
      || new Date().toISOString()
    ).trim();
    const normalizedBy = String(
      normalizationMeta?.normalized_by
      || safeOriginal.normalized_by
      || ''
    ).trim();
    const normalizedByEmail = String(
      normalizationMeta?.normalized_by_email
      || safeOriginal.normalized_by_email
      || ''
    ).trim();
    return {
      ...safeOriginal,
      ...safeNormalized,
      test_case_no: String(safeOriginal.test_case_no || safeNormalized.test_case_no || '').trim(),
      test_description:
        String(safeNormalized.test_description || safeNormalized.description || safeOriginal.test_description || safeOriginal.description || '').trim(),
      test_steps: normalizedSteps.length > 0 ? normalizedSteps : toNormalizedStepList(safeOriginal.test_steps),
      expected_result:
        String(safeNormalized.expected_result || safeNormalized.expected_results || safeOriginal.expected_result || safeOriginal.expected_results || '').trim(),
      source_type: 'manual_upload',
      _source: 'manual',
      normalization_status: 'normalized',
      normalized_at: normalizedAt,
      normalized_by: normalizedBy,
      normalized_by_email: normalizedByEmail,
      normalization_context:
        normalizationMeta?.normalization_context
        || safeNormalized.normalization_context
        || safeOriginal.normalization_context
        || null,
    };
  };

  const buildManualNormalizationContextPayload = () => {
    const rawContext = normalizationContext && typeof normalizationContext === 'object'
      ? normalizationContext
      : {};
    const rawSectionNumber =
      rawContext.section_number
      ?? rawContext.sectionNumber
      ?? processingResult?.section_number
      ?? selectedSection;
    const resolvedSectionNumber = Number(rawSectionNumber);
    const resolvedPdfId = String(
      rawContext.pdf_id
      ?? rawContext.pdfId
      ?? pdfId
      ?? ''
    ).trim();
    const resolvedFeatureSessionId = String(
      rawContext.feature_session_id
      ?? rawContext.featureSessionId
      ?? ''
    ).trim();
    const requestedContextType = String(
      rawContext.context_type
      ?? rawContext.contextType
      ?? ''
    ).trim().toLowerCase();
    const resolvedContextType =
      requestedContextType
      || (resolvedFeatureSessionId ? 'feature_capture' : '')
      || (resolvedPdfId && Number.isFinite(resolvedSectionNumber) && resolvedSectionNumber > 0 ? 'source_section' : 'none');
    const resolvedSectionTitle = String(
      rawContext.section_title
      ?? rawContext.sectionTitle
      ?? processingResult?.section_title
      ?? ''
    ).trim();
    const resolvedSourceLabel = String(
      rawContext.source_label
      ?? rawContext.sourceLabel
      ?? sourceLabelText
      ?? ''
    ).trim();
    const resolvedSourceType = String(
      rawContext.source_type
      ?? rawContext.sourceType
      ?? processingResult?.source_type
      ?? ''
    ).trim().toLowerCase();
    const resolvedApplicationId = String(
      rawContext.application_id
      ?? rawContext.applicationId
      ?? processingResult?.application_id
      ?? ''
    ).trim();

    return {
      context_type: resolvedContextType,
      ...(resolvedSourceLabel ? { source_label: resolvedSourceLabel } : {}),
      ...(resolvedSourceType ? { source_type: resolvedSourceType } : {}),
      ...(resolvedPdfId ? { pdf_id: resolvedPdfId } : {}),
      ...(Number.isFinite(resolvedSectionNumber) && resolvedSectionNumber > 0
        ? { section_number: resolvedSectionNumber }
        : {}),
      ...(resolvedSectionTitle ? { section_title: resolvedSectionTitle } : {}),
      ...(resolvedFeatureSessionId ? { feature_session_id: resolvedFeatureSessionId } : {}),
      ...(resolvedApplicationId ? { application_id: resolvedApplicationId } : {}),
    };
  };

  const getNormalizationContextSuffix = (normalizationContextMeta = {}) => {
    const meta = normalizationContextMeta && typeof normalizationContextMeta === 'object'
      ? normalizationContextMeta
      : {};
    const contextLabel = String(meta.context_label || meta.source_label || '').trim();
    if (meta.feature_context_used) {
      return ` using ${contextLabel || 'captured feature context'}.`;
    }
    if (meta.source_context_used || meta.brd_context_used) {
      return ` using ${contextLabel || 'source context'}.`;
    }
    return ' without source context.';
  };

  const applyNormalizedManualCasesToState = (
    casesState,
    selectedEntries,
    normalizedCases,
    normalizationMeta
  ) => {
    const replacements = new Map(
      selectedEntries.map((entry, idx) => [
        buildManualCaseSelectionKey(entry.category, entry.testCase, entry.index),
        mergeNormalizedManualCase(entry.testCase, normalizedCases[idx], normalizationMeta),
      ])
    );

    const nextState = cloneCases(casesState);
    ['positive', 'negative', 'all'].forEach((category) => {
      nextState[category] = (nextState[category] || []).map((testCase, index) => {
        const key = buildManualCaseSelectionKey(category, testCase, index);
        return replacements.has(key) ? replacements.get(key) : testCase;
      });
    });
    return nextState;
  };

  const normalizeManualEntries = async (selectedEntries = [], options = {}) => {
    if (!Array.isArray(selectedEntries) || selectedEntries.length === 0) {
      throw new Error('No manual test cases selected for normalization.');
    }
    if (selectedEntries.length > MAX_MANUAL_NORMALIZE_SELECTION) {
      throw new Error(`Normalize up to ${MAX_MANUAL_NORMALIZE_SELECTION} manual test cases per batch.`);
    }

    const requestPayload = selectedEntries.map(({ testCase }) => {
      const { _source, _category, _case_key, _renamed_from, ...rest } = testCase || {};
      return rest;
    });
    const normalizePayload = {
      test_cases: requestPayload,
      ...buildManualNormalizationContextPayload(),
    };

    const response = await apiFetch(API_ENDPOINTS.NORMALIZE_MANUAL_TEST_CASES_SECURE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(normalizePayload),
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload.detail || 'Failed to normalize manual test cases');
    }

    let payload = await response.json();
    if (payload?.job?.id) {
      const queuedJob = announceJob(payload.job);
      await waitForJobResult(queuedJob, { timeoutMs: 45 * 60 * 1000 });
      const resultResponse = await apiFetch(API_ENDPOINTS.JOB_RESULT(queuedJob.id));
      if (!resultResponse.ok) {
        const resultError = await resultResponse.json().catch(() => ({}));
        throw new Error(resultError.detail || 'Normalized cases could not be loaded.');
      }
      payload = await resultResponse.json();
    }
    const normalizedCases = Array.isArray(payload?.normalized_test_cases)
      ? payload.normalized_test_cases
      : [];
    if (normalizedCases.length !== selectedEntries.length) {
      throw new Error('Normalization response is incomplete. Please try again.');
    }
    const normalizedByName = String(currentUser?.name || '').trim();
    const normalizedByEmail = String(currentUser?.email || '').trim();
    const normalizationMeta = {
      normalized_at: new Date().toISOString(),
      normalized_by: normalizedByName || normalizedByEmail || 'Unknown user',
      normalized_by_email: normalizedByEmail,
      normalization_context: payload?.normalization_context || null,
    };

    const nextState = applyNormalizedManualCasesToState(
      options?.baseState || categorizedTestCases,
      selectedEntries,
      normalizedCases,
      normalizationMeta
    );

    setCategorizedTestCases(nextState);
    setHasChanges(true);
    if (options?.persist !== false) {
      await persistDraftSnapshot(
        nextState,
        options?.manualImportMetaOverride === undefined
          ? manualImportMeta
          : options.manualImportMetaOverride
      );
    }

    return {
      normalizedCount: normalizedCases.length,
      normalizationContext: payload?.normalization_context || null,
      normalizedBy: normalizationMeta.normalized_by,
      nextState,
    };
  };

  const handleNormalizeSingleManualCase = async (category, index, testCase) => {
    if (isSaving || isImporting || isNormalizingManualCases) return;
    if (isManualCaseAlreadyNormalized(testCase)) {
      setNormalizeManualStatus(null);
      setNormalizeManualError('This manual test case is already normalized.');
      return;
    }

    setNormalizeManualError(null);
    setNormalizeManualStatus(null);
    setIsNormalizingManualCases(true);
    setNormalizingManualAction('single');
    setNormalizingManualCaseKeys([buildManualCaseSelectionKey(category, testCase, index)]);
    try {
      const result = await normalizeManualEntries([{ category, index, testCase }]);
      const contextSuffix = getNormalizationContextSuffix(result.normalizationContext);
      const normalizedBySuffix = result.normalizedBy
        ? ` Normalized by ${result.normalizedBy}.`
        : '';
      const caseLabel = String(testCase?.test_case_no || '').trim();
      setNormalizeManualStatus(
        `Normalized ${caseLabel || 'selected manual test case'}${contextSuffix}${normalizedBySuffix}`
      );
    } catch (err) {
      setNormalizeManualError(err.message || 'Failed to normalize manual test case');
    } finally {
      setIsNormalizingManualCases(false);
      setNormalizingManualAction(null);
      setNormalizingManualCaseKeys([]);
    }
  };

  const handleNormalizeAllPendingManualCases = async () => {
    if (isSaving || isImporting || isNormalizingManualCases) return;

    const pendingEntries = getPendingManualCaseEntries(categorizedTestCases);
    if (pendingEntries.length === 0) {
      setNormalizeManualStatus(null);
      setNormalizeManualError('All manual test cases are already normalized.');
      return;
    }

    setNormalizeManualError(null);
    setNormalizeManualStatus(null);
    setIsNormalizingManualCases(true);
    setNormalizingManualAction('all');
    setNormalizingManualCaseKeys(
      pendingEntries.map(({ category, index, testCase }) =>
        buildManualCaseSelectionKey(category, testCase, index)
      )
    );
    try {
      let normalizedTotal = 0;
      let batchCount = 0;
      let contextSummary = { normalizationContext: null, normalizedBy: '' };
      let workingState = categorizedTestCases;
      for (let start = 0; start < pendingEntries.length; start += MAX_MANUAL_NORMALIZE_SELECTION) {
        const batch = pendingEntries.slice(start, start + MAX_MANUAL_NORMALIZE_SELECTION);
        batchCount += 1;
        const processedAfterBatch = Math.min(start + batch.length, pendingEntries.length);
        if (processedAfterBatch < pendingEntries.length) {
          setNormalizeManualStatus(
            `Normalizing manual test cases... ${processedAfterBatch}/${pendingEntries.length}`
          );
        }
        const result = await normalizeManualEntries(batch, { baseState: workingState });
        normalizedTotal += result.normalizedCount;
        workingState = result.nextState || workingState;
        contextSummary = result;
      }

      const contextSuffix = getNormalizationContextSuffix(contextSummary.normalizationContext);
      const normalizedBySuffix = contextSummary.normalizedBy
        ? ` Normalized by ${contextSummary.normalizedBy}.`
        : '';
      setNormalizeManualStatus(
        `Normalized ${normalizedTotal} manual test case${normalizedTotal === 1 ? '' : 's'} in ${batchCount} batch${batchCount === 1 ? '' : 'es'}${contextSuffix}${normalizedBySuffix}`
      );
    } catch (err) {
      setNormalizeManualError(err.message || 'Failed to normalize manual test cases');
    } finally {
      setIsNormalizingManualCases(false);
      setNormalizingManualAction(null);
      setNormalizingManualCaseKeys([]);
    }
  };

  const handleNormalizeSelectedManualCases = async () => {
    if (isSaving || isImporting || isNormalizingManualCases) return;

    const selectedEntries = selectedCaseEntries.filter(({ testCase }) => (
      testCase?._source === 'manual' && !isManualCaseAlreadyNormalized(testCase)
    ));

    if (selectedEntries.length === 0) {
      setNormalizeManualStatus(null);
      setNormalizeManualError('Select at least one pending manual test case to normalize.');
      return;
    }

    setNormalizeManualError(null);
    setNormalizeManualStatus(null);
    setIsNormalizingManualCases(true);
    setNormalizingManualAction('selected');
    setNormalizingManualCaseKeys(
      selectedEntries.map(({ category, index, testCase }) =>
        buildManualCaseSelectionKey(category, testCase, index)
      )
    );
    try {
      let normalizedTotal = 0;
      let batchCount = 0;
      let contextSummary = { normalizationContext: null, normalizedBy: '' };
      let workingState = categorizedTestCases;
      for (let start = 0; start < selectedEntries.length; start += MAX_MANUAL_NORMALIZE_SELECTION) {
        const batch = selectedEntries.slice(start, start + MAX_MANUAL_NORMALIZE_SELECTION);
        batchCount += 1;
        const processedAfterBatch = Math.min(start + batch.length, selectedEntries.length);
        if (processedAfterBatch < selectedEntries.length) {
          setNormalizeManualStatus(
            `Normalizing selected manual test cases... ${processedAfterBatch}/${selectedEntries.length}`
          );
        }
        const result = await normalizeManualEntries(batch, { baseState: workingState });
        normalizedTotal += result.normalizedCount;
        workingState = result.nextState || workingState;
        contextSummary = result;
      }

      const normalizedSelectionKeys = new Set(
        selectedEntries.map(({ category, index, testCase }) =>
          buildManualCaseSelectionKey(category, testCase, index)
        )
      );
      setSelectedCaseKeys((prev) => prev.filter((key) => !normalizedSelectionKeys.has(key)));

      const contextSuffix = getNormalizationContextSuffix(contextSummary.normalizationContext);
      const normalizedBySuffix = contextSummary.normalizedBy
        ? ` Normalized by ${contextSummary.normalizedBy}.`
        : '';
      setNormalizeManualStatus(
        `Normalized ${normalizedTotal} selected manual test case${normalizedTotal === 1 ? '' : 's'} in ${batchCount} batch${batchCount === 1 ? '' : 'es'}${contextSuffix}${normalizedBySuffix}`
      );
    } catch (err) {
      setNormalizeManualError(err.message || 'Failed to normalize selected manual test cases');
    } finally {
      setIsNormalizingManualCases(false);
      setNormalizingManualAction(null);
      setNormalizingManualCaseKeys([]);
    }
  };

  const persistDraftSnapshot = async (
    nextCasesState = categorizedTestCases,
    nextManualImportMeta = manualImportMeta,
    options = {}
  ) => {
    if (!onSaveTestCases) return null;
    const payload = sanitizeCasesForPersistence(
      testCaseStructure === 'flat'
        ? nextCasesState.all
        : nextCasesState
    );
    const baselinePayload = sanitizeCasesForPersistence(
      testCaseStructure === 'flat'
        ? baselineTestCases.all
        : baselineTestCases
    );
    const normalizedManualImportMeta = normalizeManualImportMeta(nextManualImportMeta);
    const result = await onSaveTestCases(payload, baselinePayload, {
      autosave: true,
      manualImportMeta: normalizedManualImportMeta || null,
      keepalive: Boolean(options?.keepalive),
    });
    lastDraftAutosaveSignatureRef.current = JSON.stringify({
      cases: payload,
      manual_import: normalizedManualImportMeta,
    });
    return result;
  };

  const handleImportClick = () => {
    if (manualCasesCount > 0) {
      setImportStatus(null);
      setImportError(
        `Manual test cases are already imported from ${manualImportMeta?.file_name || 'an existing file'}. Only one import file is allowed per ${manualImportScopeLabel}.`
      );
      return;
    }
    setImportError(null);
    setImportStatus(null);
    setNormalizeManualError(null);
    setNormalizeManualStatus(null);
    if (importInputRef.current) {
      importInputRef.current.value = '';
      importInputRef.current.click();
    }
  };

  const handleManualCasesImport = async (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    setImportError(null);
    setImportStatus(null);
    setNormalizeManualError(null);
    setNormalizeManualStatus(null);
    setIsImporting(true);

    try {
      if (manualCasesCount > 0) {
        throw new Error(
          `Manual test cases are already imported from ${manualImportMeta?.file_name || 'an existing file'}. Only one import file is allowed per ${manualImportScopeLabel}.`
        );
      }
      const fileName = selectedFile.name || '';
      const lowerName = fileName.toLowerCase();
      if (!lowerName.endsWith('.xlsx')) {
        throw new Error('Only .xlsx files are supported for manual import.');
      }
      const imported = await parseExcelTestCases(selectedFile);
      const mergedCurrent = mergeImportedCasesIntoState(categorizedTestCases, imported);

      const importedCount =
        imported.structure === 'categorized'
          ? imported.positive.length + imported.negative.length
          : imported.all.length;
      const nextManualImportMeta = normalizeManualImportMeta({
        file_name: selectedFile.name,
        imported_at: new Date().toISOString(),
        imported_count: importedCount,
        imported_by_name: String(currentUser?.name || '').trim() || null,
        imported_by_email: String(currentUser?.email || '').trim() || null,
        imported_by:
          String(currentUser?.name || '').trim()
          || String(currentUser?.email || '').trim()
          || null,
      });

      setCategorizedTestCases(mergedCurrent.nextState);
      setManualImportMeta(nextManualImportMeta);
      setHasChanges(true);

      try {
        await persistDraftSnapshot(mergedCurrent.nextState, nextManualImportMeta);
      } catch (syncErr) {
        setSaveError(syncErr.message || 'Failed to sync imported test cases');
      }

      const importedPendingEntries = getPendingManualCaseEntries(mergedCurrent.nextState);
      if (importedPendingEntries.length > 0) {
        setIsNormalizingManualCases(true);
        setNormalizingManualAction('import');
        setNormalizingManualCaseKeys(
          importedPendingEntries.map(({ category, index, testCase }) =>
            buildManualCaseSelectionKey(category, testCase, index)
          )
        );
        setNormalizeManualStatus(`Normalizing imported manual test cases... 0/${importedPendingEntries.length}`);
        try {
          let workingState = mergedCurrent.nextState;
          let normalizedTotal = 0;
          let contextSummary = { normalizationContext: null, normalizedBy: '' };
          for (let start = 0; start < importedPendingEntries.length; start += MAX_MANUAL_NORMALIZE_SELECTION) {
            const batch = importedPendingEntries.slice(start, start + MAX_MANUAL_NORMALIZE_SELECTION);
            const result = await normalizeManualEntries(batch, {
              baseState: workingState,
              manualImportMetaOverride: nextManualImportMeta,
            });
            normalizedTotal += result.normalizedCount;
            workingState = result.nextState || workingState;
            contextSummary = result;
            const processedAfterBatch = Math.min(start + batch.length, importedPendingEntries.length);
            if (processedAfterBatch < importedPendingEntries.length) {
              setNormalizeManualStatus(
                `Normalizing imported manual test cases... ${processedAfterBatch}/${importedPendingEntries.length}`
              );
            }
          }
          const contextSuffix = getNormalizationContextSuffix(contextSummary.normalizationContext);
          const normalizedBySuffix = contextSummary.normalizedBy
            ? ` Normalized by ${contextSummary.normalizedBy}.`
            : '';
          setImportStatus(
            `Imported and normalized ${normalizedTotal} manual test case${normalizedTotal === 1 ? '' : 's'}${contextSuffix}${normalizedBySuffix}`
          );
          setNormalizeManualStatus(null);
        } catch (normalizeErr) {
          setImportStatus(
            `Imported ${importedCount} manual test case${importedCount === 1 ? '' : 's'}. Normalize pending cases after reviewing them.`
          );
          setNormalizeManualError(normalizeErr.message || 'Failed to normalize imported manual test cases');
        } finally {
          setIsNormalizingManualCases(false);
          setNormalizingManualAction(null);
          setNormalizingManualCaseKeys([]);
        }
      } else {
        setImportStatus(
          `Imported ${importedCount} manual test case${importedCount === 1 ? '' : 's'}.`
        );
      }
    } catch (err) {
      setImportError(err.message || 'Failed to import manual test cases');
    } finally {
      setIsImporting(false);
    }
  };

  const toggleExpandCase = (category, index) => {
    const caseId = `${category}-${index}`;
    setExpandedCase(expandedCase === caseId ? null : caseId);
  };

  const handleRemoveCase = (category, caseIndex) => {
    const casesInCategory = categorizedTestCases?.[category] || [];
    const selectedCase = casesInCategory[caseIndex];
    if (!selectedCase) return;

    if (totalCases <= 1) {
      setSaveError('At least one test case must remain.');
      return;
    }

    const selectionKey = buildManualCaseSelectionKey(category, selectedCase, caseIndex);
    const guardError = getAiDeletionGuardError(categorizedTestCases, [{
      category,
      caseIndex,
      testCase: selectedCase,
      selectionKey,
    }]);
    if (guardError) {
      setSaveError(guardError);
      return;
    }

    const caseLabel = selectedCase?.test_case_no || `Test Case ${caseIndex + 1}`;
    setPendingRemoveCase({
      mode: 'single',
      category,
      caseIndex,
      caseLabel,
      caseNo: String(selectedCase?.test_case_no || '').trim(),
      selectionKey,
      entries: [
        {
          category,
          caseIndex,
          caseNo: String(selectedCase?.test_case_no || '').trim(),
          selectionKey,
        },
      ],
    });
  };

  const handleToggleCaseSelection = (category, caseIndex, testCase) => {
    const selectionKey = buildManualCaseSelectionKey(category, testCase, caseIndex);
    setSelectedCaseKeys((prev) => (
      prev.includes(selectionKey)
        ? prev.filter((key) => key !== selectionKey)
        : [...prev, selectionKey]
    ));
    setSaveError(null);
  };

  const handleSelectVisibleCases = () => {
    const visibleEntries = testCaseStructure === 'categorized'
      ? (activeCaseTabConfig?.entries || [])
      : getAllCaseEntries(categorizedTestCases);
    if (visibleEntries.length === 0) return;
    const visibleKeys = visibleEntries.map(({ category, index, testCase }) =>
      buildManualCaseSelectionKey(category, testCase, index)
    );
    setSelectedCaseKeys((prev) => (
      Array.from(new Set([...prev, ...visibleKeys]))
    ));
    setSaveError(null);
  };

  const handleClearCaseSelection = () => {
    setSelectedCaseKeys([]);
    setSaveError(null);
  };

  const handleDeleteSelectedCases = () => {
    const selectedSet = new Set(selectedCaseKeys);
    if (selectedSet.size === 0) {
      setSaveError('Select at least one test case to remove.');
      return;
    }

    const selectedEntries = getAllCaseEntries(categorizedTestCases)
      .filter(({ category, index, testCase }) =>
        selectedSet.has(buildManualCaseSelectionKey(category, testCase, index))
      )
      .map(({ category, index, testCase }) => ({
        category,
        caseIndex: index,
        caseNo: String(testCase?.test_case_no || '').trim(),
        selectionKey: buildManualCaseSelectionKey(category, testCase, index),
      }));

    if (selectedEntries.length === 0) {
      setSelectedCaseKeys([]);
      setSaveError('Selected test cases are no longer available.');
      return;
    }
    if (totalCases - selectedEntries.length < 1) {
      setSaveError('At least one test case must remain.');
      return;
    }
    const guardError = getAiDeletionGuardError(categorizedTestCases, selectedEntries);
    if (guardError) {
      setSaveError(guardError);
      return;
    }

    setPendingRemoveCase({
      mode: 'bulk',
      entries: selectedEntries,
      caseLabel: `${selectedEntries.length} selected test case${selectedEntries.length === 1 ? '' : 's'}`,
      totalCount: selectedEntries.length,
    });
  };

  const handleRemoveManualImport = () => {
    const manualEntries = getManualCaseEntries(categorizedTestCases)
      .map(({ category, index, testCase }) => ({
        category,
        caseIndex: index,
        testCase,
        caseNo: String(testCase?.test_case_no || '').trim(),
        selectionKey: buildManualCaseSelectionKey(category, testCase, index),
      }));

    if (manualEntries.length === 0) {
      setManualImportMeta(null);
      setImportError(null);
      setImportStatus(null);
      setSaveError(null);
      return;
    }

    if (totalCases - manualEntries.length < 1) {
      setSaveError('At least one test case must remain.');
      return;
    }

    const guardError = getAiDeletionGuardError(categorizedTestCases, manualEntries);
    if (guardError) {
      setSaveError(guardError);
      return;
    }

    setPendingRemoveCase({
      mode: 'manual_import',
      entries: manualEntries,
      caseLabel: `${manualEntries.length} manually imported test case${manualEntries.length === 1 ? '' : 's'}`,
      totalCount: manualEntries.length,
      fileName: manualImportMeta?.file_name || '',
    });
  };

  const confirmRemoveCase = async () => {
    if (!pendingRemoveCase) return;
    const removeEntries = Array.isArray(pendingRemoveCase.entries)
      ? pendingRemoveCase.entries
      : [];
    const removeKeys = new Set(
      removeEntries
        .map((entry) => String(entry?.selectionKey || '').trim())
        .filter(Boolean)
    );
    if (removeKeys.size === 0) {
      setPendingRemoveCase(null);
      return;
    }
    const guardError = getAiDeletionGuardError(categorizedTestCases, removeEntries);
    if (guardError) {
      setSaveError(guardError);
      setPendingRemoveCase(null);
      return;
    }

    const nextState = cloneCases(categorizedTestCases);
    ['positive', 'negative', 'all'].forEach((category) => {
      nextState[category] = (nextState[category] || []).filter((testCase, index) => {
        const selectionKey = buildManualCaseSelectionKey(category, testCase, index);
        return !removeKeys.has(selectionKey);
      });
    });
    const nextTotalCases =
      (nextState.positive?.length || 0)
      + (nextState.negative?.length || 0)
      + (nextState.all?.length || 0);
    if (nextTotalCases < 1) {
      setSaveError('At least one test case must remain.');
      setPendingRemoveCase(null);
      return;
    }

    const nextManualCount = getManualCaseEntries(nextState).length;
    const nextManualImportMeta = nextManualCount > 0 ? manualImportMeta : null;

    setCategorizedTestCases(nextState);
    setSelectedCaseKeys((prev) => prev.filter((key) => !removeKeys.has(key)));
    setManualImportMeta(nextManualImportMeta);

    setPendingRemoveCase(null);
    setExpandedCase(null);
    setHasChanges(true);
    setSaveError(null);
    setImportError(null);
    setImportStatus(null);
    setGenerateMoreError(null);
    setGenerateMoreStatus(null);
    try {
      await persistDraftSnapshot(nextState, nextManualImportMeta);
    } catch (err) {
      setSaveError(err.message || 'Failed to sync deleted test cases');
    }
  };

  useEffect(() => {
    setSelectedCaseKeys((prev) => {
      if (prev.length === 0) return prev;
      const availableEntries = testCaseStructure === 'categorized'
        ? [
          ...(categorizedTestCases.positive || []).map((testCase, index) => ({
            category: 'positive',
            index,
            testCase,
          })),
          ...(categorizedTestCases.negative || []).map((testCase, index) => ({
            category: 'negative',
            index,
            testCase,
          })),
        ]
        : (categorizedTestCases.all || []).map((testCase, index) => ({
          category: 'all',
          index,
          testCase,
        }));
      const availableKeys = new Set(
        availableEntries.map(({ category, index, testCase }) =>
          buildManualCaseSelectionKey(category, testCase, index)
        )
      );
      const filtered = prev.filter((key) => availableKeys.has(key));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [categorizedTestCases, testCaseStructure]);

  const totalCases =
    categorizedTestCases.positive.length +
    categorizedTestCases.negative.length +
    categorizedTestCases.all.length;
  const streamStatus = String(processingResult?.stream_status || '').trim().toLowerCase();
  const isStreamRunning = streamStatus === 'running';
  const isStreamComplete = streamStatus === 'completed';
  const isStreamFailed = streamStatus === 'failed';
  useEffect(() => {
    if (!onSaveTestCases || !hasChanges || isSaving || isStreamRunning || totalCases === 0) {
      return undefined;
    }

    const currentCasesPayload = sanitizeCasesForPersistence(
      testCaseStructure === 'flat'
        ? categorizedTestCases.all
        : categorizedTestCases
    );
    const currentManualImportMeta = normalizeManualImportMeta(manualImportMeta);
    const draftSignature = JSON.stringify({
      cases: currentCasesPayload,
      manual_import: currentManualImportMeta,
    });

    if (
      draftSignature === lastDraftAutosaveSignatureRef.current
      || draftSignature === draftAutosaveInFlightSignatureRef.current
    ) {
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      draftAutosaveInFlightSignatureRef.current = draftSignature;
      try {
        await persistDraftSnapshot(categorizedTestCases, currentManualImportMeta);
        lastDraftAutosaveSignatureRef.current = draftSignature;
      } catch (err) {
        setSaveError(err.message || 'Failed to sync test-case draft');
      } finally {
        if (draftAutosaveInFlightSignatureRef.current === draftSignature) {
          draftAutosaveInFlightSignatureRef.current = '';
        }
      }
    }, TEST_CASE_DRAFT_AUTOSAVE_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  // persistDraftSnapshot intentionally uses the current local draft selected by this autosave signature.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    categorizedTestCases,
    hasChanges,
    isSaving,
    isStreamRunning,
    manualImportMeta,
    onSaveTestCases,
    testCaseStructure,
    totalCases,
  ]);

  useEffect(() => {
    const flushDraftSnapshot = ({ keepalive = false } = {}) => {
      if (!onSaveTestCases || !hasChanges || isSaving || isStreamRunning || totalCases === 0) {
        return;
      }
      const currentCasesPayload = sanitizeCasesForPersistence(
        testCaseStructure === 'flat'
          ? categorizedTestCases.all
          : categorizedTestCases
      );
      const currentManualImportMeta = normalizeManualImportMeta(manualImportMeta);
      const draftSignature = JSON.stringify({
        cases: currentCasesPayload,
        manual_import: currentManualImportMeta,
      });
      if (
        draftSignature === lastDraftAutosaveSignatureRef.current
        || draftSignature === draftAutosaveInFlightSignatureRef.current
      ) {
        return;
      }
      draftAutosaveInFlightSignatureRef.current = draftSignature;
      persistDraftSnapshot(
        categorizedTestCases,
        currentManualImportMeta,
        { keepalive }
      ).catch((err) => {
        setSaveError(err.message || 'Failed to sync test-case draft');
      }).finally(() => {
        if (draftAutosaveInFlightSignatureRef.current === draftSignature) {
          draftAutosaveInFlightSignatureRef.current = '';
        }
      });
    };

    flushDraftSnapshotRef.current = flushDraftSnapshot;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushDraftSnapshot({ keepalive: true });
      }
    };
    const handlePageHide = () => flushDraftSnapshot({ keepalive: true });
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  // persistDraftSnapshot intentionally reads the latest rendered draft snapshot.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    categorizedTestCases,
    hasChanges,
    isSaving,
    isStreamRunning,
    manualImportMeta,
    onSaveTestCases,
    testCaseStructure,
    totalCases,
  ]);

  useEffect(() => () => {
    flushDraftSnapshotRef.current({ keepalive: false });
  }, []);
  const expectedPositiveCases = Math.max(
    0,
    Number(
      processingResult?.approved_counts?.positive_count
      ?? processingResult?.approved_counts?.positive
      ?? 0
    ) || 0
  );
  const expectedNegativeCases = Math.max(
    0,
    Number(
      processingResult?.approved_counts?.negative_count
      ?? processingResult?.approved_counts?.negative
      ?? 0
    ) || 0
  );
  const expectedTotalCases = expectedPositiveCases + expectedNegativeCases;
  const rawStreamedCaseCount = Number(processingResult?.streamed_case_count || 0);
  const visibleStreamedCaseCount = totalCases || rawStreamedCaseCount;
  const displayedTotalCases = Math.max(expectedTotalCases, visibleStreamedCaseCount);
  const streamedCaseCount = visibleStreamedCaseCount;
  const hasVisibleLiveCases = totalCases > 0;
  const isLiveCaseListFinalizing = isStreamRunning && !hasVisibleLiveCases && rawStreamedCaseCount > 0;
  const rawStreamedInputSetCount = Number(processingResult?.streamed_input_sets_count || 0);
  const streamedInputSetCount = totalCases > 0
    ? Math.min(rawStreamedInputSetCount, totalCases)
    : rawStreamedInputSetCount;
  const isGeneratingMore = status === 'processing';
  const requestedAdditionalCount = clampAdditionalCount(morePositive) + clampAdditionalCount(moreNegative);
  const maxAdditionalPositive = MAX_ADDITIONAL_PER_TYPE;
  const maxAdditionalNegative = MAX_ADDITIONAL_PER_TYPE;
  const canGenerateMore =
    requestedAdditionalCount > 0;
  const allCurrentCases = [
    ...categorizedTestCases.positive,
    ...categorizedTestCases.negative,
    ...categorizedTestCases.all,
  ];
  const manualCaseEntries = getManualCaseEntries(categorizedTestCases);
  const pendingManualCaseEntries = getPendingManualCaseEntries(categorizedTestCases);
  const pendingManualCasesByCategory = pendingManualCaseEntries.reduce(
    (acc, entry) => {
      if (entry.category === 'positive') acc.positive += 1;
      if (entry.category === 'negative') acc.negative += 1;
      if (entry.category === 'all') acc.all += 1;
      return acc;
    },
    { positive: 0, negative: 0, all: 0 }
  );
  const aiCasesCount = allCurrentCases.filter((testCase) => (testCase?._source || 'ai') === 'ai').length;
  const manualCasesCount = allCurrentCases.filter((testCase) => testCase?._source === 'manual').length;
  const pendingManualCasesCount = pendingManualCaseEntries.length;
  const renamedCases = allCurrentCases
    .filter((testCase) => testCase?._source === 'manual' && testCase?._renamed_from)
    .length;
  const aiPositiveCases = categorizedTestCases.positive
    .map((testCase, index) => ({ testCase, index, category: 'positive' }))
    .filter(({ testCase }) => !isUserImportedCase(testCase));
  const aiNegativeCases = categorizedTestCases.negative
    .map((testCase, index) => ({ testCase, index, category: 'negative' }))
    .filter(({ testCase }) => !isUserImportedCase(testCase));
  const manualPositiveCases = categorizedTestCases.positive
    .map((testCase, index) => ({ testCase, index, category: 'positive' }))
    .filter(({ testCase }) => normalizeSourceType(testCase?.source_type) === 'manual_upload');
  const manualNegativeCases = categorizedTestCases.negative
    .map((testCase, index) => ({ testCase, index, category: 'negative' }))
    .filter(({ testCase }) => normalizeSourceType(testCase?.source_type) === 'manual_upload');
  const excelPositiveCases = categorizedTestCases.positive
    .map((testCase, index) => ({ testCase, index, category: 'positive' }))
    .filter(({ testCase }) => isExcelImportedSourceType(testCase?.source_type));
  const excelNegativeCases = categorizedTestCases.negative
    .map((testCase, index) => ({ testCase, index, category: 'negative' }))
    .filter(({ testCase }) => isExcelImportedSourceType(testCase?.source_type));
  const hasAiCaseBucketEntries = aiPositiveCases.length + aiNegativeCases.length > 0;
  const hasManualCaseBucketEntries = manualPositiveCases.length + manualNegativeCases.length > 0;
  const hasExcelCaseBucketEntries = excelPositiveCases.length + excelNegativeCases.length > 0;
  const isExcelCaseContext = (
    /\b(excel|workbook)\b/i.test(sourceLabelText)
    || isExcelImportedSourceType(processingResult?.source_type)
    || isExcelImportedSourceType(processingResult?.item_type)
  );
  const shouldShowAiBuckets = (
    !isExcelCaseContext
    || hasAiCaseBucketEntries
    || (!hasManualCaseBucketEntries && !hasExcelCaseBucketEntries)
  );
  const shouldShowManualBuckets = enableManualImport || hasManualCaseBucketEntries;
  const shouldShowExcelBuckets = isExcelCaseContext || hasExcelCaseBucketEntries;
  const allCategorizedCaseTabs = [
    {
      id: 'ai-positive',
      label: 'AI Positive',
      sourceKind: 'ai',
      heading: 'AI Generated Positive Test Cases',
      entries: aiPositiveCases,
      icon: CheckCircle,
      countTone: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      activeTone: 'border-emerald-300 bg-emerald-50 text-emerald-800',
      inactiveTone: 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
    },
    {
      id: 'ai-negative',
      label: 'AI Negative',
      sourceKind: 'ai',
      heading: 'AI Generated Negative Test Cases',
      entries: aiNegativeCases,
      icon: AlertCircle,
      countTone: 'bg-rose-50 text-rose-700 border-rose-200',
      activeTone: 'border-rose-300 bg-rose-50 text-rose-800',
      inactiveTone: 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
    },
    {
      id: 'manual-positive',
      label: 'Manual Positive',
      sourceKind: 'manual',
      heading: 'Manual Uploaded Positive Test Cases',
      entries: manualPositiveCases,
      icon: CheckCircle,
      countTone: 'bg-amber-50 text-amber-700 border-amber-200',
      activeTone: 'border-amber-300 bg-amber-50 text-amber-800',
      inactiveTone: 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
    },
    {
      id: 'manual-negative',
      label: 'Manual Negative',
      sourceKind: 'manual',
      heading: 'Manual Uploaded Negative Test Cases',
      entries: manualNegativeCases,
      icon: AlertCircle,
      countTone: 'bg-orange-50 text-orange-700 border-orange-200',
      activeTone: 'border-orange-300 bg-orange-50 text-orange-800',
      inactiveTone: 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
    },
    {
      id: 'excel-positive',
      label: 'Excel Positive',
      sourceKind: 'excel',
      heading: 'Excel Imported Positive Test Cases',
      entries: excelPositiveCases,
      icon: CheckCircle,
      countTone: 'border-cyan-200 bg-cyan-50 text-cyan-700',
      activeTone: 'border-cyan-300 bg-cyan-50 text-cyan-800',
      inactiveTone: 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
    },
    {
      id: 'excel-negative',
      label: 'Excel Negative',
      sourceKind: 'excel',
      heading: 'Excel Imported Negative Test Cases',
      entries: excelNegativeCases,
      icon: AlertCircle,
      countTone: 'border-sky-200 bg-sky-50 text-sky-700',
      activeTone: 'border-sky-300 bg-sky-50 text-sky-800',
      inactiveTone: 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
    },
  ];
  const categorizedCaseTabs = allCategorizedCaseTabs.filter((tab) => {
    if (tab.sourceKind === 'excel') return shouldShowExcelBuckets;
    if (tab.sourceKind === 'manual') return shouldShowManualBuckets;
    return shouldShowAiBuckets;
  });
  const visibleCaseGroupSources = [
    shouldShowAiBuckets ? 'AI' : '',
    shouldShowManualBuckets ? 'manual' : '',
    shouldShowExcelBuckets ? 'Excel' : '',
  ].filter(Boolean);
  const visibleCaseGroupSourceText = visibleCaseGroupSources.length <= 2
    ? visibleCaseGroupSources.join(' and ')
    : `${visibleCaseGroupSources.slice(0, -1).join(', ')}, and ${visibleCaseGroupSources[visibleCaseGroupSources.length - 1]}`;
  const firstVisibleCaseTab = categorizedCaseTabs[0]?.id || 'ai-positive';
  const firstAvailableCaseTab = categorizedCaseTabs.find((tab) => tab.entries.length > 0)?.id || firstVisibleCaseTab;
  const hasActiveCategorizedTabCases = categorizedCaseTabs.some(
    (tab) => tab.id === activeCaseTab && tab.entries.length > 0
  );
  const resolvedActiveCaseTab = hasActiveCategorizedTabCases ? activeCaseTab : firstAvailableCaseTab;
  const activeCaseTabConfig = categorizedCaseTabs.find((tab) => tab.id === resolvedActiveCaseTab) || categorizedCaseTabs[0];
  const ActiveCaseTabIcon = activeCaseTabConfig?.icon || CheckCircle;
  const isManualTabActive = testCaseStructure === 'categorized'
    && String(resolvedActiveCaseTab || '').startsWith('manual');
  const isManualImportLocked = manualCasesCount > 0;
  const selectedCaseSet = new Set(selectedCaseKeys);
  const normalizingManualCaseSet = new Set(normalizingManualCaseKeys);
  const allCaseEntries = getAllCaseEntries(categorizedTestCases);
  const selectedCaseEntries = allCaseEntries.filter(({ category, index, testCase }) =>
    selectedCaseSet.has(buildManualCaseSelectionKey(category, testCase, index))
  );
  const selectedCaseCount = selectedCaseEntries.length;
  const selectedPendingManualCount = selectedCaseEntries.filter(({ testCase }) => (
    testCase?._source === 'manual' && !isManualCaseAlreadyNormalized(testCase)
  )).length;
  const activeEntriesForSelection = testCaseStructure === 'categorized'
    ? (activeCaseTabConfig?.entries || [])
    : allCaseEntries;
  const selectedCasesInActiveView = activeEntriesForSelection.filter(({ category, index, testCase }) =>
    selectedCaseSet.has(buildManualCaseSelectionKey(category, testCase, index))
  ).length;
  const selectedDeleteGuardError = selectedCaseCount > 0
    ? getAiDeletionGuardError(categorizedTestCases, selectedCaseEntries)
    : null;

  useEffect(() => {
    if (testCaseStructure !== 'categorized') return;
    if (activeCaseTab !== resolvedActiveCaseTab) {
      setActiveCaseTab(resolvedActiveCaseTab);
    }
  }, [activeCaseTab, resolvedActiveCaseTab, testCaseStructure]);

  const normalizationContextPreview = buildManualNormalizationContextPayload();
  const normalizationPreviewLabel = String(
    normalizationContextPreview.source_label
    || sourceLabelText
    || 'source'
  ).trim();
  const normalizationContextLabel = normalizationContextPreview.context_type === 'none'
    ? 'available manual case details'
    : `${normalizationPreviewLabel} context`;
  const currentCasesSummary = testCaseStructure === 'categorized'
    ? `${categorizedTestCases.positive.length} positive / ${categorizedTestCases.negative.length} negative`
    : `${categorizedTestCases.all.length} total`;
  const currentEditableVersion = loadedVersion ?? activeVersion ?? lastSavedVersion ?? null;
  const getArtifactBrdVersion = useCallback((artifact = null) => {
    const candidate = Number(
      artifact?.brd_version
      || artifact?.request_payload?.brd_version
      || processingResult?.brd_version
      || processingResult?.base_version
      || 0
    );
    return Number.isFinite(candidate) && candidate > 0 ? candidate : null;
  }, [processingResult?.base_version, processingResult?.brd_version]);
  const brdMappedVersionHistory = useMemo(() => {
    const grouped = new Map();
    (versionHistory || []).forEach((artifact) => {
      if (!artifact) return;
      const brdVersion = getArtifactBrdVersion(artifact) || `artifact-${artifact.version || artifact.id || grouped.size + 1}`;
      const existing = grouped.get(brdVersion);
      const artifactTime = Date.parse(artifact.updated_at || artifact.created_at || '') || 0;
      const existingTime = existing ? (Date.parse(existing.updated_at || existing.created_at || '') || 0) : 0;
      const shouldUseArtifact =
        !existing
        || artifact.version === activeVersion
        || (!existing.active && artifact.active)
        || (artifactTime >= existingTime && existing.version !== activeVersion);
      if (shouldUseArtifact) {
        grouped.set(brdVersion, {
          ...artifact,
          _brdVersion: Number(brdVersion) || getArtifactBrdVersion(artifact),
          _artifactRevisionCount: (existing?._artifactRevisionCount || 0) + 1,
        });
      } else if (existing) {
        grouped.set(brdVersion, {
          ...existing,
          _artifactRevisionCount: (existing._artifactRevisionCount || 1) + 1,
        });
      }
    });
    return Array.from(grouped.values()).sort((left, right) => {
      const leftBrd = Number(left?._brdVersion || 0);
      const rightBrd = Number(right?._brdVersion || 0);
      if (leftBrd !== rightBrd) return rightBrd - leftBrd;
      return Number(right?.version || 0) - Number(left?.version || 0);
    });
  }, [activeVersion, getArtifactBrdVersion, versionHistory]);
  const activeEditableArtifact = useMemo(
    () => (versionHistory || []).find((artifact) => Number(artifact?.version) === Number(currentEditableVersion)) || null,
    [currentEditableVersion, versionHistory]
  );
  const currentEditableBrdVersion = getArtifactBrdVersion(activeEditableArtifact);
  const versionCount = brdMappedVersionHistory.length;
  const compactVersionStatus = (() => {
    if (isSaving) return 'Saving...';
    if (hasChanges) return 'Unsaved changes';
    if (currentEditableBrdVersion) return `Saved for ${sourceLabelText} v${currentEditableBrdVersion}`;
    if (lastSavedVersion) return `Saved test cases`;
    return 'No saved version yet';
  })();
  const compactVersionMeta = [
    versionCount > 0 ? `${versionCount} ${sourceLabelText} version${versionCount === 1 ? '' : 's'} with test cases` : `No ${sourceLabelText}-mapped test cases yet`,
    lastSavedAt ? `Saved ${new Date(lastSavedAt).toLocaleString()}` : null,
  ].filter(Boolean).join(' · ');
  const hasExistingVersion = Boolean(currentEditableVersion || lastSavedVersion || versionHistory.length > 0);
  const persistActionLabel = isStreamRunning
    ? 'Generating Test Cases...'
    : hasChanges
      ? 'Save Test Cases'
      : hasExistingVersion
        ? 'Continue with Current Cases'
        : 'Save Current Cases';
  const persistActionTitle = isStreamRunning
    ? 'Wait for generation to finish before saving or continuing'
    : hasChanges
      ? `Save your edits and continue to the ${continueTargetLabel}`
      : hasExistingVersion
        ? `Use the current test cases and continue to the ${continueTargetLabel}`
        : `Save the current test cases and continue to the ${continueTargetLabel}`;
  const nextStepCopy = isStreamRunning
    ? 'You can review cases as they arrive. Saving and continuing unlock when the final list is ready.'
    : hasChanges
      ? 'Save your edits before moving to review and approval.'
      : `Continue to the ${continueTargetLabel}.`;
  const advancedActionIssueCount = [
    importError,
    generateMoreError,
  ].filter(Boolean).length;

  useEffect(() => {
    if (
      isImporting ||
      isGeneratingMore ||
      importError ||
      importStatus ||
      generateMoreError ||
      generateMoreStatus
    ) {
      setIsAdvancedActionsOpen(true);
    }
  }, [
    generateMoreError,
    generateMoreStatus,
    importError,
    importStatus,
    isGeneratingMore,
    isImporting,
  ]);

  const shouldRenderLiveEmptyState = ['pending', 'queued', 'running'].includes(enrichmentMeta.status);
  const shouldRenderEmptyManualImport = Boolean(
    allowEmptyManualImport
    && enableManualImport
    && totalCases === 0
    && !isStreamRunning
    && !isStreamFailed
    && !shouldRenderLiveEmptyState
  );
  if (
    totalCases === 0
    && !isStreamRunning
    && !isStreamFailed
    && !shouldRenderLiveEmptyState
    && !shouldRenderEmptyManualImport
  ) {
    return (
      <div className="bg-white rounded-lg p-8 text-center">
        <p className="text-gray-500">No test cases available to edit</p>
      </div>
    );
  }

  const renderObjectArray = (items) => (
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div
          key={idx}
          className="rounded border border-gray-200 bg-white px-2 py-1 text-sm text-gray-800"
        >
          {Object.entries(item).map(([key, value]) => (
            <div key={key} className="flex gap-2">
              <span className="font-semibold text-gray-700">{key}:</span>
              <span className="text-gray-800 break-words">
                {value === null || value === undefined ? '' : String(value)}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  const isFileLikeInputField = (fieldName = '') =>
    /file|upload|attachment|document|pdf|image|photo|screenshot|csv|xlsx|excel/i.test(fieldName);

  const getFileAcceptForField = (fieldName = '') => {
    if (/pdf/i.test(fieldName)) return '.pdf,application/pdf';
    if (/image|photo|screenshot/i.test(fieldName)) return 'image/*';
    if (/csv/i.test(fieldName)) return '.csv,text/csv';
    if (/xlsx|excel/i.test(fieldName)) {
      return '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';
    }
    return '';
  };

  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || '');
        const commaIndex = result.indexOf(',');
        resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
      };
      reader.onerror = () => reject(new Error('Failed to read selected file'));
      reader.readAsDataURL(file);
    });

  const shouldHideCaseDetailField = (key, value) => (
    key.startsWith('_') ||
    key === 'curl_command' ||
    key === 'expected_api_response' ||
    key === 'test_steps' ||
    key === 'expected_result' ||
    key === 'expected_results' ||
    key === 'user_inputs' ||
    key === 'user_input_sets' ||
    key === 'test_data' ||
    key === 'automation_readiness' ||
    key === 'active_input_set_index' ||
    key === 'test_case_no' ||
    key === 'test_description' ||
    key === 'User_Role' ||
    key === 'user_role' ||
    key === 'module' ||
    key === 'tab_flows' ||
    key === 'source' ||
    key === 'source_type' ||
    key === 'normalization_status' ||
    key === 'normalized_at' ||
    key === 'normalized_by' ||
    key === 'normalized_by_email' ||
    !value
  );

  const getConfiguredEnabledKeys = () => {
    const config =
      processingResult?.test_case_field_config
      || processingResult?.test_cases?.field_config
      || processingResult?.field_config
      || {};
    if (Array.isArray(config.enabled_keys)) return config.enabled_keys.map((key) => String(key || ''));
    if (Array.isArray(config.enabled_fields)) {
      return config.enabled_fields.map((field) => String(field?.key || '')).filter(Boolean);
    }
    if (Array.isArray(config.fields)) {
      return config.fields
        .filter((field) => field?.enabled || field?.required)
        .map((field) => String(field?.key || ''))
        .filter(Boolean);
    }
    return [];
  };

  const isApiTestCase = (testCase = {}) => {
    const enabledKeys = getConfiguredEnabledKeys();
    return Boolean(
      testCase?.curl_command
      || testCase?.expected_api_response
      || enabledKeys.includes('curl_command')
      || enabledKeys.includes('expected_api_response')
    );
  };

  const stringifyEditableValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value, null, 2);
    } catch (_err) {
      return String(value);
    }
  };

  const renderApiFieldEditor = (testCase, category, index, field, label, placeholder, minHeight = 'min-h-[120px]') => (
    <div>
      <label className="block text-sm font-semibold text-gray-900 mb-2">
        {label} <span className="text-red-500">*</span>
      </label>
      <textarea
        value={stringifyEditableValue(testCase?.[field])}
        onChange={(e) => handleFieldChange(category, index, field, e.target.value)}
        disabled={isSaving}
        placeholder={placeholder}
        className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical ${minHeight} font-mono text-sm disabled:bg-gray-100 disabled:cursor-not-allowed`}
      />
    </div>
  );

  const renderInputRowsEditor = (rows, category, caseIndex, setIndex) => (
    <div className="space-y-3">
      {(Array.isArray(rows) ? rows : []).map((item, itemIndex) => (
        <div
          key={`input-set-${setIndex}-row-${itemIndex}`}
          className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm transition-shadow hover:shadow-md"
        >
          <div className="mb-3 flex items-center justify-between gap-2">
            <p className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
              Field {itemIndex + 1}
            </p>
            <button
              type="button"
              onClick={() => removeInputRowFromSet(category, caseIndex, setIndex, itemIndex)}
              disabled={isSaving}
              className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Remove Row
            </button>
          </div>

          <div className="space-y-2">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[88px_minmax(0,1fr)] sm:items-center">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">Field</span>
              <input
                type="text"
                value={item?.field ?? ''}
                onChange={(e) =>
                  handleInputRowChange(category, caseIndex, setIndex, itemIndex, 'field', e.target.value)
                }
                disabled={isSaving}
                placeholder="Input field name"
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-gray-100"
              />
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[88px_minmax(0,1fr)] sm:items-start">
              <span className="pt-1 text-xs font-semibold uppercase tracking-wide text-slate-600">Value</span>
              {isFileLikeInputField(item?.field || '') ? (
                <div className="space-y-1">
                  <input
                    type="file"
                    accept={getFileAcceptForField(item?.field || '')}
                    onChange={async (e) => {
                      const selectedFile = e.target.files?.[0];
                      if (!selectedFile) {
                        handleInputRowChange(category, caseIndex, setIndex, itemIndex, 'value', '');
                        handleInputRowChange(category, caseIndex, setIndex, itemIndex, 'file_name', '');
                        handleInputRowChange(category, caseIndex, setIndex, itemIndex, 'file_mime_type', '');
                        handleInputRowChange(category, caseIndex, setIndex, itemIndex, 'file_size', '');
                        handleInputRowChange(category, caseIndex, setIndex, itemIndex, 'file_data_base64', '');
                        return;
                      }

                      let base64Data = '';
                      try {
                        base64Data = await readFileAsBase64(selectedFile);
                      } catch (err) {
                        console.error(err);
                      }

                      handleInputRowChange(category, caseIndex, setIndex, itemIndex, 'value', selectedFile?.name || '');
                      handleInputRowChange(category, caseIndex, setIndex, itemIndex, 'file_name', selectedFile?.name || '');
                      handleInputRowChange(category, caseIndex, setIndex, itemIndex, 'file_mime_type', selectedFile?.type || '');
                      handleInputRowChange(category, caseIndex, setIndex, itemIndex, 'file_size', String(selectedFile?.size || ''));
                      handleInputRowChange(category, caseIndex, setIndex, itemIndex, 'file_data_base64', base64Data);
                    }}
                    disabled={isSaving}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm file:mr-3 file:rounded file:border-0 file:bg-blue-100 file:px-2 file:py-1 file:text-blue-900 disabled:cursor-not-allowed disabled:bg-gray-100"
                  />
                  <p className="text-[11px] text-slate-600">
                    Selected: {item?.value || 'No file chosen'}
                  </p>
                </div>
              ) : (
                <input
                  type="text"
                  value={item?.value ?? ''}
                  onChange={(e) =>
                    handleInputRowChange(category, caseIndex, setIndex, itemIndex, 'value', e.target.value)
                  }
                  disabled={isSaving}
                  placeholder="Input value"
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-gray-100"
                />
              )}
            </div>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={() => addInputRowToSet(category, caseIndex, setIndex)}
        disabled={isSaving}
        className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-800 hover:bg-blue-100 disabled:opacity-50"
      >
        <Plus className="h-3.5 w-3.5" />
        Add Field Value
      </button>
    </div>
  );

  const renderUserInputSetsEditor = (testCase, category, caseIndex) => {
    const normalizedCase = normalizeUserInputSetsForCase(testCase);
    const inputSets = Array.isArray(normalizedCase.user_input_sets) ? normalizedCase.user_input_sets : [];
    const activeSetIndex = clampInputSetIndex(
      normalizedCase.active_input_set_index ?? 0,
      inputSets.length || 1
    );

    if (inputSets.length === 0) {
      return (
        <div className="rounded-xl border border-dashed border-slate-300 bg-gradient-to-r from-slate-50 to-blue-50/50 px-3 py-3">
          <p className="text-xs text-slate-600">
            No input set configured yet.
          </p>
          <button
            type="button"
            onClick={() => addInputSet(category, caseIndex)}
            disabled={isSaving}
            className="mt-2 inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-800 hover:bg-blue-100 disabled:opacity-50"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Input Set
          </button>
        </div>
      );
    }

    const activeSet = inputSets[activeSetIndex] || inputSets[0];
    const activeSetFieldCount = Array.isArray(activeSet?.user_inputs) ? activeSet.user_inputs.length : 0;

    return (
      <div className="space-y-3">
        <div className="rounded-xl border border-blue-100 bg-gradient-to-r from-blue-50/80 via-white to-cyan-50/70 px-3 py-3 shadow-sm">
          <div className="flex flex-wrap items-center gap-2">
            {inputSets.map((_, setIndex) => (
              <button
                key={`set-tab-${setIndex}`}
                type="button"
                onClick={() => handleActiveInputSetChange(category, caseIndex, setIndex)}
                disabled={isSaving}
                className={`inline-flex items-center justify-center rounded-full border px-3 py-1 text-xs font-semibold ${activeSetIndex === setIndex
                    ? 'border-blue-500 bg-blue-600 text-white shadow-sm'
                    : 'border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:text-blue-700'
                  } disabled:opacity-50`}
              >
                Set {setIndex + 1}
              </button>
            ))}
            <button
              type="button"
              onClick={() => addInputSet(category, caseIndex)}
              disabled={isSaving || inputSets.length >= MAX_INPUT_SETS_PER_CASE}
              className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" />
              Add
            </button>
            <button
              type="button"
              onClick={() => removeInputSet(category, caseIndex, activeSetIndex)}
              disabled={isSaving || inputSets.length <= 1}
              className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Remove
            </button>
          </div>
          <p className="mt-2 text-[11px] text-slate-600">
            New sets copy field names and keep values empty.
          </p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              Editing Set {activeSetIndex + 1}
            </p>
            <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] text-slate-600">
              {activeSetFieldCount} fields
            </span>
          </div>

          {renderInputRowsEditor(activeSet?.user_inputs || [], category, caseIndex, activeSetIndex)}
        </div>
      </div>
    );
  };

  const renderTestCaseCard = (testCase, index, category) => {
    const caseId = `${category}-${index}`;
    const isExpanded = expandedCase === caseId;
    const isInputSetsExpanded = Boolean(expandedInputSetsByCase[caseId]);
    const normalizedCase = normalizeUserInputSetsForCase(testCase);
    const inputSetCount = Array.isArray(normalizedCase.user_input_sets)
      ? normalizedCase.user_input_sets.length
      : 0;
    const iconColor =
      category === 'positive'
        ? 'text-green-600'
        : category === 'negative'
          ? 'text-red-600'
          : 'text-gray-500';
    const CategoryIcon = category === 'positive'
      ? CheckCircle
      : category === 'negative'
        ? AlertCircle
        : CheckCircle;
    const isLiveCase = Boolean(testCase?._streaming || testCase?._streaming_input_sets);
    const isApiCase = isApiTestCase(testCase);

    const isManualCase = testCase?._source === 'manual';
    const isExcelCase = (
      testCase?._source === 'excel'
      || isExcelImportedSourceType(testCase?.source_type)
    );
    const isAiGenerated = !isManualCase && !isExcelCase;
    const sourceBadge = isExcelCase
      ? {
        label: 'Excel Import',
        className: 'bg-cyan-100 text-cyan-800',
      }
      : isManualCase
        ? {
          label: 'Manual Upload',
          className: 'bg-amber-100 text-amber-800',
        }
        : {
          label: 'AI Generated',
          className: 'bg-blue-100 text-blue-800',
        };
    const excelSource = (
      testCase?.source && typeof testCase.source === 'object'
        ? testCase.source
        : testCase?.source_provenance && typeof testCase.source_provenance === 'object'
          ? testCase.source_provenance
          : testCase?.provenance && typeof testCase.provenance === 'object'
            ? testCase.provenance
            : {}
    );
    const excelSourceSheet = String(
      excelSource.sheet_name || excelSource.sheet || ''
    ).trim();
    const excelSourceRow = Number(
      excelSource.row || (Array.isArray(excelSource.row_numbers) ? excelSource.row_numbers[0] : 0)
    );
    const isCaseEdited = isEditedCase(testCase);
    const generatedAtLabel = formatTimestamp(
      testCase?.generated_at ||
      testCase?.created_at ||
      resolvedGeneratedAt
    );
    const generatedByLabel = resolveActorLabel(
      testCase?.generated_by_name,
      testCase?.generated_by_email,
      testCase?.generated_by,
      testCase?.request_payload?.generated_by_name,
      testCase?.request_payload?.generated_by_email,
      testCase?.request_payload?.generated_by,
      testCase?.payload?.generated_by_name,
      testCase?.payload?.generated_by_email,
      testCase?.payload?.generated_by,
      resolvedGeneratedBy
    );
    const caseEditedAtLabel = formatTimestamp(
      testCase?.edited_at ||
      testCase?.request_payload?.edited_at ||
      testCase?.payload?.edited_at
    );
    const caseEditedByLabel = resolveActorLabel(
      testCase?.edited_by_name,
      testCase?.edited_by_email,
      testCase?.edited_by,
      testCase?.request_payload?.edited_by_name,
      testCase?.request_payload?.edited_by_email,
      testCase?.request_payload?.edited_by,
      testCase?.payload?.edited_by_name,
      testCase?.payload?.edited_by_email,
      testCase?.payload?.edited_by
    );
    const editedAtLabel = caseEditedAtLabel || (isCaseEdited ? formatTimestamp(resolvedEditedAt) : null);
    const editedByLabel = caseEditedByLabel || (isCaseEdited ? resolvedEditedBy : null);
    const editedByDisplayLabel = editedByLabel || (isCaseEdited ? currentActorLabel : null);
    const manualImportedAtLabel = formatTimestamp(
      testCase?.imported_at ||
      testCase?.request_payload?.imported_at ||
      testCase?.payload?.imported_at ||
      manualImportMeta?.imported_at
    );
    const manualImportedByLabel = resolveActorLabel(
      testCase?.imported_by_name,
      testCase?.imported_by_email,
      testCase?.imported_by,
      testCase?.request_payload?.imported_by_name,
      testCase?.request_payload?.imported_by_email,
      testCase?.request_payload?.imported_by,
      testCase?.payload?.imported_by_name,
      testCase?.payload?.imported_by_email,
      testCase?.payload?.imported_by,
      manualImportMeta?.imported_by_name,
      manualImportMeta?.imported_by_email,
      manualImportMeta?.imported_by
    );
    const manualImportedFileLabel = String(
      testCase?.imported_file_name ||
      testCase?.request_payload?.imported_file_name ||
      testCase?.payload?.imported_file_name ||
      manualImportMeta?.file_name ||
      ''
    ).trim();
    const isNormalizedManualCase = (
      isManualCase
      && String(testCase?.normalization_status || '').trim().toLowerCase() === 'normalized'
    );
    const normalizedAtLabel = formatTimestamp(testCase?.normalized_at);
    const normalizedByLabel = String(
      testCase?.normalized_by || testCase?.normalized_by_email || ''
    ).trim();
    const selectionKey = buildManualCaseSelectionKey(category, testCase, index);
    const isCaseSelected = selectedCaseKeys.includes(selectionKey);
    const isCaseNormalizing = normalizingManualCaseSet.has(selectionKey);
    const singleDeleteGuardError = getAiDeletionGuardError(categorizedTestCases, [{
      category,
      index,
      testCase,
      selectionKey,
    }]);
    const removeCaseDisabledReason = totalCases <= 1
      ? 'At least one test case must remain'
      : singleDeleteGuardError;

    return (
      <div
        key={caseId}
        className={`rounded-lg border bg-white shadow-sm transition-all hover:shadow-md ${isCaseSelected
            ? 'border-blue-300 ring-1 ring-blue-200'
            : isLiveCase
              ? 'border-blue-200 ring-1 ring-blue-100'
            : 'border-gray-200'
          }`}
      >
        <div
          onClick={() => {
            if (!isSaving) {
              toggleExpandCase(category, index);
            }
          }}
          className={`w-full cursor-pointer px-6 py-4 flex items-start justify-between gap-4 transition-colors ${isCaseSelected ? 'bg-blue-50/40 hover:bg-blue-50/60' : 'hover:bg-gray-50'
            }`}
        >
          <div className="flex min-w-0 flex-1 items-start gap-4 text-left">
            <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full">
              <CategoryIcon className={`w-5 h-5 ${iconColor}`} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="font-semibold text-gray-900">
                  {resolveCaseHeaderTitle(testCase, index, isExcelCase)}
                </h4>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-semibold ${sourceBadge.className}`}
                >
                  {sourceBadge.label}
                </span>
                {inputSetCount > 0 && (
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-semibold ${inputSetCount > 1
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-slate-100 text-slate-700'
                      }`}
                    title={`${inputSetCount} input set${inputSetCount === 1 ? '' : 's'} configured`}
                  >
                    Input Sets: {inputSetCount}
                  </span>
                )}
                {isLiveCase && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-800">
                    <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
                    Live
                  </span>
                )}
                {inputSetCount === 0 && isAiGenerated && isEnrichmentPending && (
                  <span
                    className="px-2 py-0.5 rounded-full bg-cyan-50 text-xs font-semibold text-cyan-800"
                    title="Input fields and variants will be added after core generation finishes"
                  >
                    Input data pending
                  </span>
                )}
                {isCaseEdited && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                    {editedByDisplayLabel ? `Edited by ${editedByDisplayLabel}` : 'Edited by user'}
                  </span>
                )}
                {isNormalizedManualCase && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-violet-100 text-violet-800">
                    Normalized
                  </span>
                )}
                <label
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${isCaseSelected
                      ? 'border-blue-300 bg-blue-100 text-blue-800'
                      : 'border-slate-300 bg-white text-slate-700'
                    }`}
                  onClick={(event) => event.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={isCaseSelected}
                    onChange={() => handleToggleCaseSelection(category, index, testCase)}
                    disabled={isSaving || isImporting || isNormalizingManualCases}
                    className="h-3.5 w-3.5 rounded border-slate-400 text-blue-600 focus:ring-blue-500"
                  />
                  Select
                </label>
                {isManualCase && (
                  <button
                    type="button"
                    className={`ml-1 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${isNormalizedManualCase
                        ? 'cursor-not-allowed border-violet-300 bg-violet-100 text-violet-800'
                        : 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
                      }`}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleNormalizeSingleManualCase(category, index, testCase);
                    }}
                    disabled={
                      isSaving
                      || isImporting
                      || isNormalizingManualCases
                      || isNormalizedManualCase
                    }
                  >
                    {isNormalizedManualCase ? (
                      <>
                        <CheckCircle className="h-3 w-3" />
                        Normalized
                      </>
                    ) : isCaseNormalizing ? (
                      <>
                        <Loader className="h-3 w-3 animate-spin" />
                        Normalizing...
                      </>
                    ) : (
                      <>
                        <Wand2 className="h-3 w-3" />
                        Normalize
                      </>
                    )}
                  </button>
                )}
              </div>
              {testCase.test_description && (
                <p className="mt-1 text-sm text-gray-600 break-words">{testCase.test_description}</p>
              )}
              {isNormalizedManualCase && (
                <p className="mt-1 text-xs text-violet-700 break-words">
                  Normalized{normalizedAtLabel ? ` at ${normalizedAtLabel}` : ''}
                  {normalizedByLabel ? ` by ${normalizedByLabel}` : ''}
                </p>
              )}
              {isManualCase && (manualImportedAtLabel || manualImportedByLabel || manualImportedFileLabel) && (
                <p className="mt-1 text-xs text-amber-700 break-words">
                  {manualImportedAtLabel ? `Imported At: ${manualImportedAtLabel}` : 'Imported'}
                  {manualImportedByLabel ? ` · Imported by: ${manualImportedByLabel}` : ''}
                  {manualImportedFileLabel ? ` · Source: ${manualImportedFileLabel}` : ''}
                </p>
              )}
              {isExcelCase && (excelSourceSheet || excelSourceRow > 0) && (
                <p className="mt-1 break-words text-xs text-cyan-700">
                  Imported from {excelSourceSheet || 'Excel workbook'}
                  {excelSourceRow > 0 ? ` · Row ${excelSourceRow}` : ''}
                </p>
              )}
              {isAiGenerated && (generatedAtLabel || generatedByLabel) && (
                <p className="mt-1 text-xs text-gray-500 break-words">
                  {generatedAtLabel ? `Generated At: ${generatedAtLabel}` : 'Generated At: Unknown'}
                  {generatedByLabel ? ` · Generated by: ${generatedByLabel}` : ''}
                </p>
              )}
              {(editedAtLabel || editedByDisplayLabel) && (
                <p className="mt-1 text-xs text-slate-500 break-words">
                  {editedAtLabel ? `Last Edited: ${editedAtLabel}` : 'Last Edited'}
                  {editedByDisplayLabel ? ` · Edited by: ${editedByDisplayLabel}` : ''}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              toggleExpandCase(category, index);
            }}
            disabled={isSaving}
            aria-label={isExpanded ? 'Collapse test case' : 'Expand test case'}
            aria-expanded={isExpanded}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isExpanded ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </button>
        </div>

        {isExpanded && (
          <div className="border-t border-gray-200 px-6 py-4 space-y-4 bg-gray-50">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => handleRemoveCase(category, index)}
                disabled={isSaving || Boolean(removeCaseDisabledReason)}
                title={removeCaseDisabledReason || 'Remove this test case'}
                className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
                Remove Test Case
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {isExcelCase && (
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Module / Feature
                  </label>
                  <input
                    type="text"
                    value={testCase.module ?? testCase.Module ?? ''}
                    onChange={(e) =>
                      handleFieldChange(category, index, 'module', e.target.value)
                    }
                    disabled={isSaving}
                    placeholder="Module or feature"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
              )}

              {(testCase.User_Role || testCase.user_role || isExcelCase) && (
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    User Role
                  </label>
                  <input
                    type="text"
                    value={testCase.User_Role ?? testCase.user_role ?? ''}
                    onChange={(e) =>
                      handleFieldChange(category, index, 'user_role', e.target.value)
                    }
                    disabled={isSaving}
                    placeholder="User role or persona"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
              )}

              {(testCase.tab_flows || isExcelCase) && (
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Tab Flows
                  </label>
                  <input
                    type="text"
                    value={testCase.tab_flows}
                    onChange={(e) =>
                      handleFieldChange(category, index, 'tab_flows', e.target.value)
                    }
                    disabled={isSaving}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
              )}

              {Object.entries(testCase).map(([key, value]) => {
                if (
                  isExcelCase
                  && [
                    'Module',
                    'module',
                    'user_role',
                    'preconditions',
                    'test_data',
                    'expected_results',
                    'source_fields',
                    'redacted_fields',
                    'automation_readiness',
                    'category',
                  ].includes(key)
                ) {
                  return null;
                }
                if (shouldHideCaseDetailField(key, value)) {
                  return null;
                }

                return (
                  <div
                    key={key}
                    className="bg-white p-3 rounded border border-gray-200"
                  >
                    <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">
                      {key.replace(/_/g, ' ')}
                    </label>
                    {Array.isArray(value) && value.every((item) => item && typeof item === 'object')
                      ? renderObjectArray(value)
                      : (
                        <p className="text-sm text-gray-800 break-words">
                          {Array.isArray(value) ? value.join(', ') : String(value)}
                        </p>
                      )}
                  </div>
                );
              })}
            </div>

            <div className="space-y-4 pt-4 border-t border-gray-300">
              {isExcelCase && !isApiCase && (
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Test Case Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={testCase.test_description || ''}
                    onChange={(e) =>
                      handleFieldChange(category, index, 'test_description', e.target.value)
                    }
                    disabled={isSaving}
                    placeholder="Describe the scenario or test condition"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical min-h-[90px] text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
              )}

              {isExcelCase && !isApiCase && (
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Preconditions
                  </label>
                  <textarea
                    value={
                      Array.isArray(testCase.preconditions)
                        ? testCase.preconditions.join('\n')
                        : testCase.preconditions || ''
                    }
                    onChange={(e) =>
                      handleFieldChange(category, index, 'preconditions', e.target.value)
                    }
                    disabled={isSaving}
                    placeholder="Enter preconditions (one per line)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical min-h-[90px] text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
              )}

              {isApiCase && (
                <div className="space-y-4 rounded-xl border border-indigo-100 bg-white p-4 shadow-sm">
                  {renderApiFieldEditor(
                    testCase,
                    category,
                    index,
                    'test_description',
                    'Test Case Description',
                    'Describe the API scenario',
                    'min-h-[90px]'
                  )}
                  {renderApiFieldEditor(
                    testCase,
                    category,
                    index,
                    'curl_command',
                    'cURL Request',
                    "curl -X POST 'BASE_URL/path' ...",
                    'min-h-[150px]'
                  )}
                  {renderApiFieldEditor(
                    testCase,
                    category,
                    index,
                    'expected_api_response',
                    'Expected API Response',
                    'HTTP 200 with response JSON/assertions',
                    'min-h-[150px]'
                  )}
                </div>
              )}

              <div className="rounded-xl border border-slate-200 bg-gradient-to-b from-white to-slate-50/60 p-3 shadow-sm">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <label className="block text-sm font-semibold text-gray-900">
                    User Input Sets (up to 3){inputSetCount > 0 ? ` • ${inputSetCount}` : ''}
                  </label>
                  <button
                    type="button"
                    onClick={() => toggleInputSetsPanel(caseId)}
                    disabled={isSaving}
                    className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold ${isInputSetsExpanded
                        ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
                        : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                      } disabled:opacity-50`}
                  >
                    {isInputSetsExpanded ? (
                      <>
                        <ChevronUp className="h-3.5 w-3.5" />
                        Hide Inputs{inputSetCount > 0 ? ` (${inputSetCount})` : ''}
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-3.5 w-3.5" />
                        Show Inputs{inputSetCount > 0 ? ` (${inputSetCount})` : ''}
                      </>
                    )}
                  </button>
                </div>
                {isInputSetsExpanded ? (
                  renderUserInputSetsEditor(testCase, category, index)
                ) : (
                  <div className="rounded-lg border border-dashed border-slate-300 bg-gradient-to-r from-slate-50 to-white px-3 py-2 text-xs text-slate-600">
                    Input rows are hidden. Click <span className="font-semibold">Show Inputs</span> to edit values.
                  </div>
                )}
              </div>

              {!isApiCase && (
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Test Steps <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={
                    Array.isArray(testCase.test_steps)
                      ? testCase.test_steps.join('\n')
                      : testCase.test_steps || ''
                  }
                  onChange={(e) =>
                    handleFieldChange(category, index, 'test_steps', e.target.value)
                  }
                  disabled={isSaving}
                  placeholder="Enter test steps (one per line)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical min-h-[120px] font-mono text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
              )}

              {!isApiCase && (
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Expected Result <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={testCase.expected_result || ''}
                  onChange={(e) =>
                    handleFieldChange(category, index, 'expected_result', e.target.value)
                  }
                  disabled={isSaving}
                  placeholder="Enter expected result"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical min-h-[100px] font-mono text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSourceSection = (sourceLabel, sourceType) => {
    const isManual = sourceType === 'manual_upload';
    const sourcePredicate = (testCase) =>
      isManual
        ? testCase?.source_type === 'manual_upload'
        : testCase?.source_type !== 'manual_upload';

    const headerClass = isManual
      ? 'text-amber-800'
      : 'text-blue-800';

    if (testCaseStructure === 'categorized') {
      const sourcePositive = categorizedTestCases.positive
        .map((testCase, index) => ({ testCase, index }))
        .filter(({ testCase }) => sourcePredicate(testCase));
      const sourceNegative = categorizedTestCases.negative
        .map((testCase, index) => ({ testCase, index }))
        .filter(({ testCase }) => sourcePredicate(testCase));
      const total = sourcePositive.length + sourceNegative.length;

      if (total === 0) return null;

      return (
        <div className="space-y-4">
          <h3 className={`text-xl font-semibold ${headerClass}`}>
            {sourceLabel} ({total})
          </h3>

          {sourcePositive.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Positive Test Cases ({sourcePositive.length})
              </h4>
              <div className="space-y-3">
                {sourcePositive.map(({ testCase, index }) =>
                  renderTestCaseCard(testCase, index, 'positive')
                )}
              </div>
            </div>
          )}

          {sourceNegative.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                Negative Test Cases ({sourceNegative.length})
              </h4>
              <div className="space-y-3">
                {sourceNegative.map(({ testCase, index }) =>
                  renderTestCaseCard(testCase, index, 'negative')
                )}
              </div>
            </div>
          )}
        </div>
      );
    }

    const sourceAll = categorizedTestCases.all
      .map((testCase, index) => ({ testCase, index }))
      .filter(({ testCase }) => sourcePredicate(testCase));

    if (sourceAll.length === 0) return null;

    return (
      <div className="space-y-3">
        <h3 className={`text-xl font-semibold ${headerClass}`}>
          {sourceLabel} ({sourceAll.length})
        </h3>
        <div className="space-y-3">
          {sourceAll.map(({ testCase, index }) =>
            renderTestCaseCard(testCase, index, 'all')
          )}
        </div>
      </div>
    );
  };

  const renderLiveCaseSkeletons = () => {
    const skeletonCount = Math.min(Math.max(expectedTotalCases || 1, 1), 3);
    const rows = Array.from({ length: skeletonCount }, (_, index) => index);
    return (
      <div className="space-y-3">
        {rows.map((row) => (
          <div
            key={`live-case-skeleton-${row}`}
            className="rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm"
          >
            <div className="flex items-start gap-4">
              <div className="mt-1 h-8 w-8 shrink-0 rounded-full bg-slate-100" />
              <div className="min-w-0 flex-1 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="h-4 w-28 rounded bg-slate-200" />
                  <div className="h-5 w-24 rounded-full bg-blue-50" />
                  <div className="h-5 w-28 rounded-full bg-emerald-50" />
                </div>
                <div className="h-3 w-full max-w-2xl rounded bg-slate-200" />
                <div className="h-3 w-3/5 rounded bg-slate-100" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderCategorizedTabsSection = () => (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 py-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-xl font-semibold text-slate-900">Test Case Groups</h3>
              <p className="mt-1 text-sm text-slate-600">
                {isLiveGenerationActive
                  ? 'New cases appear here as soon as they are ready.'
                  : `Switch between ${visibleCaseGroupSourceText || 'available'} buckets to review one focused list at a time.`}
              </p>
            </div>
            {isLiveGenerationActive && (
              <div className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-900">
                <span className="font-semibold">{streamedCaseCount}</span>
                <span className="text-blue-700"> received</span>
                {expectedTotalCases > 0 && (
                  <span className="text-blue-700"> / {expectedTotalCases} planned</span>
                )}
              </div>
            )}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {categorizedCaseTabs.map((tab) => {
              const isActive = tab.id === resolvedActiveCaseTab;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveCaseTab(tab.id)}
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    isActive ? tab.activeTone : tab.inactiveTone
                  } ${isLiveGenerationActive && tab.entries.length === 0 ? 'opacity-70' : ''}`}
                >
                  <span>{tab.label}</span>
                  <span className={`rounded-full border px-2 py-0.5 text-[11px] ${tab.countTone}`}>
                    {tab.entries.length}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-3 px-4 py-4">
          {activeCaseTabConfig ? (
            <>
              <h4 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <ActiveCaseTabIcon className="w-5 h-5 text-slate-600" />
                {activeCaseTabConfig.heading} ({activeCaseTabConfig.entries.length})
              </h4>
              {activeCaseTabConfig.entries.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
                  <span className="font-semibold text-slate-700">
                    Selected: {selectedCaseCount}
                  </span>
                  <span className="text-slate-500">
                    In this tab: {selectedCasesInActiveView}
                  </span>
                  <button
                    type="button"
                    onClick={handleSelectVisibleCases}
                    disabled={isSaving || isImporting || isLiveGenerationActive}
                    className="inline-flex items-center rounded-full border border-slate-300 bg-white px-3 py-1 font-semibold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Select Visible
                  </button>
                  <button
                    type="button"
                    onClick={handleClearCaseSelection}
                    disabled={selectedCaseCount === 0 || isSaving || isImporting || isLiveGenerationActive}
                    className="inline-flex items-center rounded-full border border-slate-300 bg-white px-3 py-1 font-semibold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteSelectedCases}
                    disabled={selectedCaseCount === 0 || isSaving || isImporting || isLiveGenerationActive || Boolean(selectedDeleteGuardError)}
                    title={
                      selectedCaseCount === 0
                        ? 'Select at least one test case to remove.'
                        : (selectedDeleteGuardError || 'Remove selected test cases')
                    }
                    className="inline-flex items-center rounded-full border border-red-300 bg-red-50 px-3 py-1 font-semibold text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Delete Selected
                  </button>
                </div>
              )}
              {activeCaseTabConfig.entries.length === 0 ? (
                isLiveGenerationActive ? (
                  isLiveCaseListFinalizing ? (
                    <div className="flex items-center justify-between gap-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-blue-700">
                          <Sparkles className="h-4 w-4" />
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-blue-950">
                            Finalizing generated cases
                          </p>
                          <p className="mt-1 text-xs text-blue-800">
                            Cases are generated. Input data is being attached and the list will appear automatically.
                          </p>
                        </div>
                      </div>
                      <Loader className="h-4 w-4 shrink-0 animate-spin text-blue-700" />
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="mb-4 flex items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-blue-100 bg-white text-blue-700">
                            <Sparkles className="h-4 w-4" />
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-950">
                              Preparing first case
                            </p>
                            <p className="mt-1 text-xs text-slate-600">
                              This area will fill automatically as cases are created.
                            </p>
                          </div>
                        </div>
                        <Loader className="h-4 w-4 shrink-0 animate-spin text-blue-700" />
                      </div>
                      <div className="animate-pulse">
                        {renderLiveCaseSkeletons()}
                      </div>
                    </div>
                  )
                ) : (
                  <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-600">
                    No test cases in this tab yet.
                  </div>
                )
              ) : (
                <div className="space-y-3">
                  {activeCaseTabConfig.entries.map(({ testCase, index, category }) =>
                    renderTestCaseCard(testCase, index, category)
                  )}
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );

  const renderImportSection = () => {
    const manualImportedByLabel = resolveActorLabel(
      manualImportMeta?.imported_by_name,
      manualImportMeta?.imported_by_email,
      manualImportMeta?.imported_by
    );
    return (
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_16px_40px_-30px_rgba(15,23,42,0.9)]">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-r from-slate-100 via-blue-50 to-cyan-50" />
        <div className="relative border-b border-slate-200 px-4 py-3">
          <div className="flex items-start gap-3">
            <div className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-blue-100 bg-white text-blue-700 shadow-sm">
              <Upload className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <h4 className="font-semibold text-slate-900">Import Manual Test Cases</h4>
              <p className="mt-1 text-sm text-slate-600">
                Merge external cases without changing AI-generated ones.
              </p>
            </div>
          </div>
        </div>

        <div className="relative space-y-3 px-4 py-3">
          <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-2.5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Supported formats
            </p>
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              <span className="rounded-full border border-slate-300 bg-white px-2 py-0.5 font-semibold text-slate-700">.xlsx</span>
            </div>
            <p className="mt-2 text-xs text-slate-600">
              Use sheet names containing <span className="font-semibold">Positive Testcases</span> and <span className="font-semibold">Negative Testcases</span> to map cases correctly.
            </p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-2.5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Import policy
            </p>
            <p className="mt-2 text-xs text-slate-600">
              Only one manual import file is allowed per {manualImportScopeLabel}.
            </p>
          </div>

          {isManualImportLocked && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <p className="font-semibold">One manual import file is already linked to this {manualImportScopeLabel}.</p>
              <p className="mt-1">
                File: <span className="font-semibold">{manualImportMeta?.file_name || 'Existing manual import'}</span>
                {manualImportMeta?.imported_count > 0 ? ` • ${manualImportMeta.imported_count} cases` : ''}
              </p>
              {manualImportMeta?.imported_at && (
                <p className="mt-1">Imported at: {formatTimestamp(manualImportMeta.imported_at)}</p>
              )}
              {manualImportedByLabel && (
                <p className="mt-1">
                  Imported by: {manualImportedByLabel}
                </p>
              )}
              <button
                type="button"
                onClick={handleRemoveManualImport}
                disabled={isSaving || isImporting || isLiveGenerationActive}
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs font-semibold text-rose-700 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Remove Manual Import
              </button>
            </div>
          )}

          <button
            onClick={handleImportClick}
            disabled={isSaving || isImporting || isManualImportLocked}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-blue-200 bg-gradient-to-r from-blue-600 to-cyan-600 px-4 py-2 font-semibold text-white shadow-sm transition hover:from-blue-700 hover:to-cyan-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isImporting ? <Loader className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {isImporting ? 'Importing...' : (isManualImportLocked ? 'Manual Import Locked' : 'Upload Manual Cases')}
          </button>

        </div>

        <input
          ref={importInputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={handleManualCasesImport}
          className="hidden"
          disabled={isSaving || isImporting || isManualImportLocked}
        />

        {importError && (
          <div className="mx-4 mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {importError}
          </div>
        )}
        {!importError && importStatus && (
          <div className="mx-4 mb-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
            {importStatus}
          </div>
        )}
      </div>
    );
  };

  const renderEmptyManualImport = () => (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
            <Upload className="h-5 w-5" />
          </span>
          <div>
            <h3 className="font-semibold text-slate-900">Upload manual test cases</h3>
            <p className="text-xs text-slate-500">XLSX</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleImportClick}
          disabled={isSaving || isImporting}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isImporting ? <Loader className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {isImporting ? 'Uploading...' : 'Choose file'}
        </button>
      </div>
      <input
        ref={importInputRef}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={handleManualCasesImport}
        className="hidden"
        disabled={isSaving || isImporting}
      />
      {importError && (
        <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {importError}
        </div>
      )}
      {!importError && importStatus && (
        <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          {importStatus}
        </div>
      )}
    </div>
  );

  const renderManualNormalizationSection = () => {
    if (!isManualTabActive) return null;
    const hasManualCases = manualCaseEntries.length > 0;
    const hasFeedback = Boolean(normalizeManualError || normalizeManualStatus);
    if (!hasManualCases && !hasFeedback) return null;

    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-2.5">
            <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-amber-300 bg-white text-amber-700">
              <Sparkles className="h-4 w-4" />
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-900">Manual Case Normalization</p>
              <p className="text-xs text-amber-800">
                Normalize pending manual cases in automatic batches of {MAX_MANUAL_NORMALIZE_SELECTION} using {normalizationContextLabel}.
              </p>
            </div>
          </div>
          <span className="rounded-full border border-amber-300 bg-white px-2.5 py-0.5 text-[11px] font-semibold text-amber-900">
            Pending {pendingManualCasesCount}
          </span>
        </div>

        {hasManualCases ? (
          <>
            <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-semibold text-amber-900">
              <span className="rounded-full border border-amber-300 bg-white px-2 py-0.5">
                Pending: {pendingManualCasesCount}
              </span>
              <span className="rounded-full border border-blue-300 bg-white px-2 py-0.5 text-blue-800">
                Selected pending: {selectedPendingManualCount}
              </span>
              {testCaseStructure === 'categorized' && (
                <>
                  <span className="rounded-full border border-emerald-300 bg-white px-2 py-0.5">
                    +ve pending: {pendingManualCasesByCategory.positive}
                  </span>
                  <span className="rounded-full border border-rose-300 bg-white px-2 py-0.5">
                    -ve pending: {pendingManualCasesByCategory.negative}
                  </span>
                </>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleNormalizeSelectedManualCases}
                disabled={
                  selectedPendingManualCount === 0
                  || isSaving
                  || isImporting
                  || isNormalizingManualCases
                }
                title={
                  selectedPendingManualCount === 0
                    ? 'Select pending manual test cases to normalize.'
                    : `Normalize selected manual test cases in batches of ${MAX_MANUAL_NORMALIZE_SELECTION}.`
                }
                className="inline-flex items-center gap-2 rounded-lg border border-blue-300 bg-blue-600 px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {normalizingManualAction === 'selected' ? (
                  <>
                    <Loader className="h-3.5 w-3.5 animate-spin" />
                    Normalizing selected...
                  </>
                ) : (
                  <>
                    <Wand2 className="h-3.5 w-3.5" />
                    Normalize Selected (Batch {MAX_MANUAL_NORMALIZE_SELECTION})
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleNormalizeAllPendingManualCases}
                disabled={
                  pendingManualCasesCount === 0
                  || isSaving
                  || isImporting
                  || isNormalizingManualCases
                }
                className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-gradient-to-r from-amber-600 via-orange-600 to-amber-700 px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:from-amber-700 hover:via-orange-700 hover:to-amber-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {normalizingManualAction === 'all' ? (
                  <>
                    <Loader className="h-3.5 w-3.5 animate-spin" />
                    Normalizing pending...
                  </>
                ) : (
                  <>
                    <Wand2 className="h-3.5 w-3.5" />
                    Normalize All Pending (Batch {MAX_MANUAL_NORMALIZE_SELECTION})
                  </>
                )}
              </button>
            </div>
          </>
        ) : (
          <div className="mt-3 rounded-lg border border-amber-200 bg-white px-3 py-2 text-xs text-amber-800">
            Import manual test cases first, then normalize them from this panel.
            <button
              type="button"
              onClick={() => setIsAdvancedActionsOpen(true)}
              className="ml-2 inline-flex items-center rounded-md border border-amber-300 bg-amber-100 px-2 py-0.5 font-semibold text-amber-900 hover:bg-amber-200"
            >
              Open Advanced Actions
            </button>
          </div>
        )}

        {normalizeManualError && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{normalizeManualError}</span>
          </div>
        )}
        {normalizeManualStatus && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
            <CheckCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{normalizeManualStatus}</span>
          </div>
        )}
      </div>
    );
  };

  const isLiveInputDataRunning = isStreamRunning && isEnrichmentPending && streamedCaseCount > 0;
  const inputReadyCount = allCurrentCases.filter((testCase) => (
    Array.isArray(testCase?.user_input_sets) && testCase.user_input_sets.length > 0
  )).length;
  const isLiveGenerationActive = isStreamRunning || (isEnrichmentPending && totalCases === 0);
  const caseProgressPercent = displayedTotalCases > 0
    ? Math.min(100, Math.round((Math.min(streamedCaseCount, displayedTotalCases) / displayedTotalCases) * 100))
    : (streamedCaseCount > 0 ? 55 : 12);
  const inputProgressPercent = totalCases > 0
    ? Math.min(100, Math.round((inputReadyCount / totalCases) * 100))
    : 0;
  const liveStageLabel = isStreamFailed
    ? 'Needs attention'
    : isStreamComplete
      ? 'Ready'
      : isLiveInputDataRunning
        ? 'Adding input data'
        : streamedCaseCount > 0
          ? 'Creating cases'
          : 'Preparing';
  const liveStatusTitle = isStreamFailed
    ? 'Generation Needs Attention'
    : isStreamComplete
      ? 'Test Cases Ready'
      : isLiveInputDataRunning
        ? 'Adding Input Data'
        : 'Creating Test Cases';
  const liveStatusDescription = (() => {
    if (isStreamFailed) {
      return processingResult?.stream_error || 'Generation stopped before the final list was saved.';
    }
    if (isStreamComplete) {
      return 'The final list is ready to review, edit, and save.';
    }
    if (isLiveInputDataRunning && streamedInputSetCount > 0) {
      return `${streamedInputSetCount} test case${streamedInputSetCount === 1 ? '' : 's'} updated with input data. More details will attach as they finish.`;
    }
    if (isLiveInputDataRunning) {
      return `${streamedCaseCount} test case${streamedCaseCount === 1 ? '' : 's'} ready. Input data is being added now.`;
    }
    if (streamedCaseCount > 0) {
      return `${streamedCaseCount} test case${streamedCaseCount === 1 ? '' : 's'} ready. New cases will keep appearing below.`;
    }
    return 'Preparing the first test case. New cases will appear below as soon as they are ready.';
  })();

  if (shouldRenderEmptyManualImport) {
    return renderEmptyManualImport();
  }

  return (
    <div className="flex flex-col gap-6">
      {!isLiveGenerationActive && (
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-l-4 border-blue-500 p-4 rounded">
          <div className="flex items-start gap-3">
            <Edit2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-gray-900">Edit Test Cases</h3>
              <p className="text-sm text-gray-600 mt-1">
                Review and edit the generated test cases. You can modify manual steps,
                expected results, and test input field values used in automation.
                {processingResult?.section_title && (
                  <span className="block mt-2 text-sm font-medium text-gray-700">
                    Module: {processingResult.section_title}
                  </span>
                )}
                <span className="block mt-1 font-medium">
                  {testCaseStructure === 'categorized' ? (
                    <>
                      Total: {categorizedTestCases.positive.length} positive,{' '}
                      {categorizedTestCases.negative.length} negative
                    </>
                  ) : (
                    <>Total: {categorizedTestCases.all.length} test cases</>
                  )}
                </span>
              </p>
            </div>
          </div>
        </div>
      )}

      {(isLiveGenerationActive || isStreamComplete || isStreamFailed) && (
        <div
          className={`overflow-hidden rounded-2xl border shadow-sm ${
            isStreamFailed
              ? 'border-rose-200 bg-rose-50'
              : isStreamComplete
                ? 'border-emerald-200 bg-emerald-50'
                : 'border-blue-200 bg-white'
          }`}
        >
          <div className="border-b border-slate-200 bg-gradient-to-r from-slate-50 via-white to-cyan-50 px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 items-start gap-3">
                <div
                  className={`mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border bg-white ${
                    isStreamFailed
                      ? 'border-rose-200 text-rose-700'
                      : isStreamComplete
                        ? 'border-emerald-200 text-emerald-700'
                        : 'border-blue-200 text-blue-700'
                  }`}
                >
                  {isStreamFailed ? (
                    <AlertCircle className="h-4 w-4" />
                  ) : isStreamComplete ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <Loader className="h-4 w-4 animate-spin" />
                  )}
                </div>
                <div className="min-w-0">
                  <h4 className="font-semibold text-slate-950">
                    {liveStatusTitle}
                  </h4>
                  <p className="mt-1 text-sm text-slate-600">
                    {liveStatusDescription}
                  </p>
                </div>
              </div>
              <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                {liveStageLabel}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <p className="text-[11px] font-semibold uppercase text-slate-500">Test Cases</p>
              <p className="mt-1 text-2xl font-bold text-slate-950">
                {streamedCaseCount}
                {displayedTotalCases > 0 && <span className="text-sm font-semibold text-slate-500"> / {displayedTotalCases}</span>}
              </p>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-blue-600 transition-all" style={{ width: `${caseProgressPercent}%` }} />
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <p className="text-[11px] font-semibold uppercase text-slate-500">Input Data</p>
              <p className="mt-1 text-2xl font-bold text-slate-950">
                {inputReadyCount}
                {totalCases > 0 && <span className="text-sm font-semibold text-slate-500"> / {totalCases}</span>}
              </p>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-cyan-600 transition-all" style={{ width: `${inputProgressPercent}%` }} />
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <p className="text-[11px] font-semibold uppercase text-slate-500">Planned Mix</p>
              <div className="mt-2 flex gap-2 text-sm font-semibold">
                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-emerald-800">
                  + {expectedPositiveCases || categorizedTestCases.positive.length}
                </span>
                <span className="rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-rose-800">
                  - {expectedNegativeCases || categorizedTestCases.negative.length}
                </span>
              </div>
              <p className="mt-3 text-xs text-slate-600">Editing tools unlock when the final list is ready.</p>
            </div>
          </div>
        </div>
      )}

      {!isLiveGenerationActive && !isStreamComplete && !isStreamFailed && (isEnrichmentPending || isEnrichmentComplete || isEnrichmentFailed || enrichmentNotice) && (
        <div
          className={`rounded-xl border p-4 ${
            isEnrichmentFailed
              ? 'border-rose-200 bg-rose-50'
              : isEnrichmentComplete
                ? 'border-emerald-200 bg-emerald-50'
                : 'border-cyan-200 bg-cyan-50'
          }`}
        >
          <div className="flex items-start gap-3">
            {isEnrichmentPending ? (
              <Loader className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-cyan-700" />
            ) : isEnrichmentFailed ? (
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-700" />
            ) : (
              <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
            )}
            <div>
              <h4
                className={`font-semibold ${
                  isEnrichmentFailed
                    ? 'text-rose-900'
                    : isEnrichmentComplete
                      ? 'text-emerald-900'
                      : 'text-cyan-900'
                }`}
              >
                {isEnrichmentFailed
                  ? 'Input Data Enrichment Failed'
                  : isEnrichmentComplete
                    ? 'Input Data Ready'
                    : 'Input Data Preparing'}
              </h4>
              <p
                className={`mt-1 text-sm ${
                  isEnrichmentFailed
                    ? 'text-rose-800'
                    : isEnrichmentComplete
                      ? 'text-emerald-800'
                      : 'text-cyan-800'
                }`}
              >
                {isEnrichmentFailed
                  ? 'Core test cases are available. Input-set variants and gap analysis can be regenerated later.'
                  : isEnrichmentComplete
                    ? (enrichmentNotice || `Input-set variants and gap analysis are available${enrichmentCompletedAt ? ` as of ${enrichmentCompletedAt}` : ''}.`)
                    : 'Core test cases are ready. Input-set variants and gap analysis are attaching live, so you can keep reviewing without waiting.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {showSourceBreakdown && (!isLiveGenerationActive || totalCases > 0) && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            <h4 className="font-semibold text-slate-900">AI vs Manual Difference</h4>
            <p className="text-xs font-medium text-slate-500">Source breakdown</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
              <p className="text-[11px] text-blue-700 font-semibold uppercase tracking-wide">AI Generated</p>
              <p className="text-3xl font-bold text-blue-900 mt-1">{aiCasesCount}</p>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-[11px] text-amber-700 font-semibold uppercase tracking-wide">Manual Uploaded</p>
              <p className="text-3xl font-bold text-amber-900 mt-1">{manualCasesCount}</p>
            </div>
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
              <p className="text-[11px] text-indigo-700 font-semibold uppercase tracking-wide">Duplicate IDs Renamed</p>
              <p className="text-3xl font-bold text-indigo-900 mt-1">{renamedCases}</p>
            </div>
          </div>
        </div>
      )}

      {hasCoverageAudit && !isLiveGenerationActive && (
        <div className="order-3 rounded-xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <h4 className="flex items-center gap-2 font-semibold text-slate-900">
                <Sparkles className="h-4 w-4 text-blue-600" />
                Coverage Health
              </h4>
              <p className="mt-1 text-sm text-slate-600">
                LLM audit results from the review pass.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${coverageScoreTone}`}>
                {coverageScore === null ? 'Score pending' : `${coverageScore}% score`}
              </span>
              <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-800">
                +{missingCoverageCounts.positive} / -{missingCoverageCounts.negative} suggested
              </span>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Missing Areas</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{missingAreas.length || coverageGaps.length}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Gap Notes</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{coverageGaps.length}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Duplicates</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{duplicateCaseIds.length}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Weak Cases</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{weakCaseIds.length}</p>
            </div>
          </div>

          {(missingAreas.length > 0 || coverageGaps.length > 0) && (
            <div className="mt-4 grid gap-3 lg:grid-cols-2">
              {missingAreas.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                    Missing Coverage
                  </p>
                  <div className="mt-2 space-y-2">
                    {missingAreas.slice(0, 4).map((area, index) => (
                      <div key={`${area.type}-${index}`} className="rounded-md border border-amber-200 bg-white/80 px-3 py-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold uppercase text-amber-800">
                            {area.type}
                          </span>
                          {area.suggested_count > 0 && (
                            <span className="text-[11px] font-semibold text-amber-700">
                              {area.suggested_count} suggested
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-sm leading-5 text-slate-700">{area.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {coverageGaps.length > 0 && (
                <div className="rounded-lg border border-blue-200 bg-blue-50/70 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-800">
                    Gap Prompts
                  </p>
                  <div className="mt-2 space-y-2">
                    {coverageGaps.slice(0, 4).map((gap, index) => (
                      <p key={`${gap}-${index}`} className="rounded-md border border-blue-200 bg-white/80 px-3 py-2 text-sm leading-5 text-slate-700">
                        {gap}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {(duplicateCaseIds.length > 0 || weakCaseIds.length > 0) && (
            <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold">
              {duplicateCaseIds.slice(0, 8).map((caseId) => (
                <span key={`duplicate-${caseId}`} className="rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-rose-700">
                  Duplicate: {caseId}
                </span>
              ))}
              {weakCaseIds.slice(0, 8).map((caseId) => (
                <span key={`weak-${caseId}`} className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-amber-800">
                  Weak: {caseId}
                </span>
              ))}
            </div>
          )}

          <div className="mt-4 flex flex-col gap-2 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-slate-600">
              {missingCoverageFocusItems.length > 0
                ? `${missingCoverageFocusItems.length} audit focus item${missingCoverageFocusItems.length === 1 ? '' : 's'} will be sent to the next LLM pass automatically.`
                : 'No missing coverage focus items were returned.'}
            </p>
            <span className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-800">
              {status === 'processing' || status === 'bulk_processing' ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  Auto-generating...
                </>
              ) : (
                <>
                  <Wand2 className="h-4 w-4" />
                  Auto Coverage Enabled
                </>
              )}
            </span>
          </div>
        </div>
      )}

      {hasAdvancedActions && (
      <div className="order-2 rounded-xl border border-slate-200 bg-white shadow-sm">
        <button
          type="button"
          onClick={() => {
            if (isAdvancedActionsOpen && isGenerateMoreModalOpen) {
              closeGenerateMoreModal();
            }
            setIsAdvancedActionsOpen((prev) => !prev);
          }}
          disabled={isLiveGenerationActive}
          className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:opacity-80"
          aria-expanded={isAdvancedActionsOpen && !isLiveGenerationActive}
        >
          <div className="min-w-0">
            <h4 className="font-semibold text-slate-900">Advanced Actions</h4>
            <p className="mt-1 text-sm text-slate-600">
              {isLiveGenerationActive
                ? 'Available when the final list is ready.'
                : onGenerateMore && enableManualImport
                  ? 'Add more AI cases and import manual test cases.'
                  : onGenerateMore
                    ? 'Add more AI cases.'
                    : 'Import manual test cases.'}
            </p>
            <div className="mt-2 flex flex-wrap gap-2 text-xs font-semibold">
              {onGenerateMore && (
                <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-blue-700">
                  Add More AI
                </span>
              )}
              {enableManualImport && (
                <span className="rounded-full border border-cyan-200 bg-cyan-50 px-2 py-0.5 text-cyan-700">
                  Import Manual
                </span>
              )}
              {advancedActionIssueCount > 0 && (
                <span className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-red-700">
                  {advancedActionIssueCount} issue{advancedActionIssueCount === 1 ? '' : 's'}
                </span>
              )}
              {isLiveGenerationActive && (
                <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-slate-600">
                  Preparing
                </span>
              )}
            </div>
          </div>
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600">
            {isAdvancedActionsOpen && !isLiveGenerationActive ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </span>
        </button>

        {isLiveGenerationActive && (
          <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            These actions unlock when the final list is ready, so incoming cases do not shift while you review.
          </div>
        )}

        {isAdvancedActionsOpen && !isLiveGenerationActive && (
          <div className="border-t border-slate-200 px-4 py-4">
            {onGenerateMore && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-12">
                  <div className="relative self-start h-fit overflow-hidden rounded-2xl border border-blue-200/80 bg-white p-4 shadow-[0_18px_45px_-30px_rgba(37,99,235,0.9)] lg:col-span-7">
                    <div className="pointer-events-none absolute -right-24 -top-20 h-52 w-52 rounded-full bg-blue-100/70 blur-3xl" />
                    <div className="pointer-events-none absolute -left-20 -bottom-20 h-44 w-44 rounded-full bg-cyan-100/80 blur-3xl" />
                    <div className="relative flex flex-col gap-3">
                      <div>
                        <h4 className="flex items-center gap-2 text-lg font-semibold text-blue-950">
                          <Wand2 className="w-5 h-5" />
                          Add More Test Cases
                        </h4>
                        <p className="mt-1 text-sm text-blue-900/80">
                          Generate only missing AI scenarios.
                        </p>
                      </div>

                      <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-700">
                          Before you generate
                        </p>
                        <div className="mt-2 space-y-1.5 text-sm text-blue-900/90">
                          <p className="flex items-start gap-2">
                            <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-blue-700" />
                            Existing test cases stay unchanged; new AI cases are appended.
                          </p>
                          <p className="flex items-start gap-2">
                            <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-blue-700" />
                            Up to {MAX_ADDITIONAL_PER_TYPE} positive and {MAX_ADDITIONAL_PER_TYPE} negative cases can be added in one run.
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:items-center sm:justify-between">
                        <div className="rounded-xl border border-blue-200 bg-white/95 px-3 py-2 text-sm shadow-sm">
                          <p className="text-xs uppercase tracking-wide text-blue-600">Current cases</p>
                          <p className="mt-1 font-semibold text-blue-950">{currentCasesSummary}</p>
                        </div>
                        <button
                          type="button"
                          onClick={openGenerateMoreModal}
                          disabled={isSaving || isGeneratingMore}
                          className="inline-flex min-w-[140px] whitespace-nowrap items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:from-blue-700 hover:to-cyan-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isGeneratingMore ? (
                            <>
                              <Loader className="w-4 h-4 animate-spin" />
                              Regenerating...
                            </>
                          ) : (
                            <>
                              <Wand2 className="w-4 h-4" />
                              Add Cases
                            </>
                          )}
                        </button>
                      </div>

                      {generateMoreError && !isGenerateMoreModalOpen && (
                        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                          {generateMoreError}
                        </div>
                      )}
                      {generateMoreStatus && (
                        <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                          {generateMoreStatus}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="self-start lg:col-span-5">
                    {enableManualImport && renderImportSection()}
                  </div>
                </div>

                {isGenerateMoreModalOpen && ReactDOM.createPortal(
                  <div
                    className="modal-overlay"
                    role="presentation"
                    onClick={closeGenerateMoreModal}
                  >
                    <div
                      className="modal-shell flex max-h-[90vh] max-w-xl flex-col"
                      role="dialog"
                      aria-modal="true"
                      aria-labelledby="add-more-cases-title"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <div className="border-b border-blue-300/40 bg-gradient-to-r from-blue-600 to-cyan-600 p-5 text-white">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <h4
                              id="add-more-cases-title"
                              className="text-xl font-semibold flex items-center gap-2"
                            >
                              <Wand2 className="h-5 w-5" />
                              Add More Test Cases
                            </h4>
                            <p className="mt-1 text-sm text-blue-100">
                              Generate additional scenarios and append them to this list.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={closeGenerateMoreModal}
                            disabled={isGeneratingMore}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/35 bg-white/10 text-white hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label="Close add more cases dialog"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <div className="space-y-4 overflow-y-auto p-5">
                        <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
                          <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                            Current cases
                          </p>
                          <p className="mt-1 text-sm font-semibold text-slate-900">{currentCasesSummary}</p>
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <div>
                            <label className="block text-xs font-semibold uppercase tracking-wide text-blue-700 mb-1">
                              Additional Positive
                            </label>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => updateMorePositive(Number(morePositive || 0) - 1)}
                                disabled={isSaving || isGeneratingMore || Number(morePositive || 0) <= 0}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="Decrease additional positive count"
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <input
                                type="number"
                                min={0}
                                max={maxAdditionalPositive}
                                value={morePositive}
                                onChange={(e) => updateMorePositive(e.target.value)}
                                disabled={isSaving || isGeneratingMore}
                                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-center font-semibold text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              />
                              <button
                                type="button"
                                onClick={() => updateMorePositive(Number(morePositive || 0) + 1)}
                                disabled={isSaving || isGeneratingMore || Number(morePositive || 0) >= MAX_ADDITIONAL_PER_TYPE}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="Increase additional positive count"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold uppercase tracking-wide text-blue-700 mb-1">
                              Additional Negative
                            </label>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => updateMoreNegative(Number(moreNegative || 0) - 1)}
                                disabled={isSaving || isGeneratingMore || Number(moreNegative || 0) <= 0}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="Decrease additional negative count"
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <input
                                type="number"
                                min={0}
                                max={maxAdditionalNegative}
                                value={moreNegative}
                                onChange={(e) => updateMoreNegative(e.target.value)}
                                disabled={isSaving || isGeneratingMore}
                                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-center font-semibold text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              />
                              <button
                                type="button"
                                onClick={() => updateMoreNegative(Number(moreNegative || 0) + 1)}
                                disabled={isSaving || isGeneratingMore || Number(moreNegative || 0) >= MAX_ADDITIONAL_PER_TYPE}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="Increase additional negative count"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-blue-700">
                          Maximum {MAX_ADDITIONAL_PER_TYPE} positive and {MAX_ADDITIONAL_PER_TYPE} negative test cases per request.
                        </p>

                        <div>
                          <div className="mb-1 flex items-center justify-between gap-3">
                            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600">
                              Missing coverage prompt (optional)
                            </label>
                            <button
                              type="button"
                              onClick={applyCoverageGapsToPrompt}
                              disabled={!hasCoverageGaps || isSaving || isGeneratingMore}
                              className="inline-flex items-center rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              Use Coverage Gaps
                            </button>
                          </div>
                          <textarea
                            value={coveragePrompt}
                            onChange={(e) => {
                              setCoveragePrompt(e.target.value);
                              setGenerateMoreError(null);
                              setGenerateMoreStatus(null);
                            }}
                            placeholder="Example: Add more negative cases for retry limits, duplicate uploads, and metadata mismatch."
                            rows={4}
                            disabled={isSaving || isGeneratingMore}
                            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
                          />
                          <p className="mt-1 text-xs text-slate-500">
                            Separate multiple focus points with commas or new lines.
                            {hasCoverageGaps
                              ? ` ${coverageGaps.length} gap item${coverageGaps.length > 1 ? 's' : ''} available to auto-fill.`
                              : ' No coverage gaps were reported in the previous step.'}
                          </p>
                        </div>

                        {generateMoreError && (
                          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                            {generateMoreError}
                          </div>
                        )}
                        {generateMoreStatus && (
                          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                            {generateMoreStatus}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4">
                        <button
                          type="button"
                          onClick={closeGenerateMoreModal}
                          disabled={isGeneratingMore}
                          className="secondary-btn px-4 py-2"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleGenerateMoreCases}
                          disabled={!canGenerateMore || isSaving || isGeneratingMore}
                          className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-600 px-4 py-2 font-semibold text-white hover:from-blue-700 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isGeneratingMore ? (
                            <>
                              <Loader className="w-4 h-4 animate-spin" />
                              Regenerating...
                            </>
                          ) : (
                            <>
                              <Wand2 className="w-4 h-4" />
                              Generate More
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>,
                  document.body
                )}
              </div>
            )}

            {!onGenerateMore && enableManualImport && (
              <div>
                {renderImportSection()}
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {versioningEnabled && isSaveOptionsOpen && ReactDOM.createPortal(
        <div
          className="modal-overlay"
          role="presentation"
          onClick={closeSaveOptionsModal}
        >
          <div
            className="modal-shell flex max-w-md flex-col"
            role="dialog"
            aria-modal="true"
            aria-labelledby="save-test-cases-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="border-b border-blue-200 bg-gradient-to-r from-blue-600 to-cyan-600 p-5 text-white">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h4
                    id="save-test-cases-title"
                    className="text-lg font-semibold flex items-center gap-2"
                  >
                    <Save className="h-5 w-5" />
                    Save Test Cases
                  </h4>
                  <p className="mt-2 text-sm text-blue-100">
                    Saving updates the test cases for the current {sourceLabelText} version. New test-case versions are created when a changed {sourceLabelText} is regenerated.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeSaveOptionsModal}
                  disabled={isSaving}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/35 bg-white/10 text-white hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Close save options dialog"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-3 bg-white p-5 text-sm text-slate-700">
              <p>
                Current {sourceLabelText} mapping: <span className="font-semibold">{currentEditableBrdVersion ? `${sourceLabelText} v${currentEditableBrdVersion}` : 'none'}</span>
              </p>
              <p>
                {sourceLabelText} versions with test cases: <span className="font-semibold">{versionCount}</span>
              </p>
              <p className="text-slate-500">
                Cancel closes this dialog without saving anything.
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4">
              <button
                type="button"
                onClick={closeSaveOptionsModal}
                disabled={isSaving}
                className="secondary-btn px-4 py-2"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  closeSaveOptionsModal();
                  await executeSaveEdits();
                }}
                disabled={isSaving}
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-600 px-4 py-2 font-semibold text-white hover:from-blue-700 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                Save Current {sourceLabelText} Test Cases
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {pendingRemoveCase && ReactDOM.createPortal(
        <div
          className="modal-overlay"
          role="presentation"
          onClick={closeRemoveCaseModal}
        >
          <div
            className="modal-shell flex max-w-md flex-col"
            role="dialog"
            aria-modal="true"
            aria-labelledby="remove-case-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="border-b border-rose-100 bg-rose-50 p-5 text-slate-900">
              <h4
                id="remove-case-title"
                className="text-lg font-semibold flex items-center gap-2"
              >
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-white text-rose-700 ring-1 ring-rose-200">
                  <AlertCircle className="h-4 w-4" />
                </span>
                {pendingRemoveCase?.mode === 'manual_import'
                  ? 'Remove Manual Import'
                  : pendingRemoveCase?.mode === 'bulk'
                    ? 'Remove Selected Test Cases'
                    : 'Remove Test Case'}
              </h4>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                {pendingRemoveCase?.mode === 'manual_import' ? (
                  <>
                    This will remove <span className="font-semibold">{pendingRemoveCase.caseLabel}</span>
                    {pendingRemoveCase.fileName ? (
                      <> from <span className="font-semibold">{pendingRemoveCase.fileName}</span></>
                    ) : null}
                    {' '}and unlock manual import for this section.
                  </>
                ) : (
                  <>
                    This will remove <span className="font-semibold">{pendingRemoveCase.caseLabel}</span>.
                  </>
                )}
                {' '}The change is synced to your saved draft automatically.
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 bg-white px-5 py-4">
              <button
                type="button"
                onClick={closeRemoveCaseModal}
                disabled={isSaving}
                className="secondary-btn px-4 py-2"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmRemoveCase}
                disabled={isSaving}
                className="inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-600 px-4 py-2 font-semibold text-white hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 className="h-4 w-4" />
                {pendingRemoveCase?.mode === 'manual_import'
                  ? 'Remove Import'
                  : pendingRemoveCase?.mode === 'bulk'
                    ? 'Remove Selected'
                    : 'Remove'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {(error || saveError) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-red-900">Error</h4>
            <p className="text-sm text-red-700 mt-1">{error || saveError}</p>
          </div>
        </div>
      )}

      {versioningEnabled && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <History className="w-4 h-4" />
                {sourceLabelText}-Mapped Test Cases
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {compactVersionMeta}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${isSaving
                  ? 'bg-blue-50 text-blue-700'
                  : hasChanges
                    ? 'bg-amber-50 text-amber-700'
                    : 'bg-slate-100 text-slate-700'
                }`}>
                {compactVersionStatus}
              </span>
              <button
                type="button"
                onClick={() => setIsVersionPanelOpen((prev) => !prev)}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                {isVersionPanelOpen ? 'Hide Versions' : 'View Versions'}
                {isVersionPanelOpen ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
              </button>
            </div>
          </div>

          {isVersionPanelOpen && (
            <div className="mt-4 border-t border-slate-200 pt-4">
              <p className="text-xs text-slate-500">
                Versions are saved only when you click Save Test Cases. Each saved version is measured against the initial AI baseline for this section.
              </p>

              {versionError && (
                <p className="mt-3 text-sm text-red-600">{versionError}</p>
              )}

              <div className="mt-4 space-y-2">
                {isLoadingVersions ? (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Loader className="w-4 h-4 animate-spin" />
                    Loading version history...
                  </div>
                ) : brdMappedVersionHistory.length === 0 ? (
                  <p className="text-sm text-slate-500">No {sourceLabelText}-mapped test cases yet for this section.</p>
                ) : (
                  brdMappedVersionHistory.map((artifact) => {
                    const saveMode = String(artifact?.request_payload?.save_mode || 'generated')
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, (char) => char.toUpperCase());
                    const baselineMatch = artifact?.metrics?.overall?.overall_average_similarity;
                    const changedAiCases = artifact?.metrics?.overall?.statistics?.test_cases_with_changes;
                    const manualAdditions = artifact?.metrics?.manual_uploaded?.added_count;
                    const artifactBrdVersion = artifact?._brdVersion || getArtifactBrdVersion(artifact);
                    const artifactRevisionCount = Number(artifact?._artifactRevisionCount || 1);
                    return (
                      <div
                        key={artifact.id || artifact.version}
                        className={`rounded-lg border px-3 py-3 ${artifact.version === activeVersion
                            ? 'border-blue-300 bg-blue-50'
                            : 'border-slate-200 bg-slate-50'
                          }`}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-slate-900">
                              {artifactBrdVersion ? `${sourceLabelText} v${artifactBrdVersion} test cases` : `Saved test cases`}
                              {artifact.version === activeVersion ? ' · Active' : ''}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                              {artifact.updated_at ? new Date(artifact.updated_at).toLocaleString() : 'Unknown time'} · {saveMode}
                              {artifactRevisionCount > 1 && ` · ${artifactRevisionCount} internal saves collapsed`}
                            </p>
                            {typeof baselineMatch === 'number' && (
                              <p className="mt-1 text-xs text-slate-600">
                                Match vs initial AI: {baselineMatch.toFixed(2)}%
                                {typeof changedAiCases === 'number' && ` · ${changedAiCases} AI case${changedAiCases === 1 ? '' : 's'} changed`}
                                {Number(manualAdditions || 0) > 0 && ` · ${manualAdditions} manual addition${manualAdditions === 1 ? '' : 's'}`}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => activateArtifactVersion(artifact.version)}
                              disabled={artifact.version === activeVersion || isSaving}
                              className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${artifact.version === activeVersion
                                  ? 'cursor-default border border-slate-200 bg-slate-100 text-slate-500'
                                  : 'border border-blue-300 bg-blue-600 text-white hover:bg-blue-700'
                              } disabled:opacity-100`}
                            >
                              {artifact.version === activeVersion ? `Current ${sourceLabelText} Test Cases` : `Open ${sourceLabelText} Test Cases`}
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteArtifactVersion(artifact)}
                              disabled={isSaving}
                              className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {renderManualNormalizationSection()}

      {testCaseStructure === 'categorized'
        ? renderCategorizedTabsSection()
        : (
          <>
            {renderSourceSection('AI Generated Test Cases', 'ai_generated')}
            {renderSourceSection('Manual Uploaded Test Cases', 'manual_upload')}
          </>
        )}

      {/* Action Buttons */}
      {!isLiveGenerationActive && <div className="order-4 flex flex-col gap-4 border-t border-gray-200 pt-6 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-900">Choose your next step</p>
          <p className="mt-1 text-sm text-slate-500">{nextStepCopy}</p>
          {hasChanges && !isSaving && (
            <span className="mt-2 inline-flex items-center gap-1 text-sm text-amber-600">
              <span className="h-2 w-2 rounded-full bg-amber-600"></span>
              Unsaved local changes
            </span>
          )}
        </div>
        <div className="flex flex-wrap justify-end gap-3">
          <button
            type="button"
            onClick={handlePersistAndContinue}
            disabled={isSaving || isStreamRunning || totalCases === 0}
            className="flex items-center gap-2 px-6 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-600 text-white transition-all font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            title={persistActionTitle}
          >
            {isSaving || isStreamRunning ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                {isStreamRunning ? 'Generating...' : 'Saving...'}
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {persistActionLabel}
              </>
            )}
          </button>
        </div>
      </div>}
    </div>
  );
};
